import { api } from '@/services/api';
import i18n from 'i18next';
import { isAxiosError } from 'axios';
import type {
  EmployeeHistoryResponse,
  EmployeeHistoryFilter,
  EmployeeHistoryFacets,
} from '@features/users/types';
import type {
  Device,
  DeviceSfpDefault,
  DeviceFilter,
  DeviceListResponse,
  CreateDeviceRequest,
  UpdateDeviceRequest,
  DeviceInterface,
  FDBEntry,
  DeviceActivityLogResponse,
  MacSearchResult,
  DeviceObserverResponse,
  DeviceObserverConfig,
  DevicePayload,
  DeviceInventoryIntegration,
  DeviceCommutationListItem,
  CreateInventoryIntegrationPayload,
  DevicePonPortStatsResponse,
  DeviceVlansResponse,
  CreateVlanRequest,
  CreateVlanResponse,
  UpdateVlanRequest,
  UpdateVlanResponse,
  DevicePonPortStat,
  DeviceIPAddress,
  DeviceFastLink,
  RadioPeerFilter,
  RadioPeerListResponse,
  PingResponse,
  SnmpInfo,
  DeviceStatistics,
  DeviceHealth,
  BackupDeviceConfigurationRequest,
  BackupDeviceConfigurationResponse,
  RestoreDeviceConfigurationRequest,
  RestoreDeviceConfigurationResponse,
  GetDeviceConfigurationHistoryResponse,
  NotificationEmployee,
} from '../types';

export type ARPReadSource = 'direct' | 'legacy_v4_redis' | 'v5_owned_cache';
export type ARPProvenance = 'direct_or_short_cache' | 'legacy_v4_redis' | 'v5_receipt_fenced_v4_payload';
export type ARPFreshness = 'unknown';
export type ARPTransport = 'Connect' | 'REST' | 'HTTP';
export type ARPOrderField = 'ip_address' | 'mac_address' | 'vlan_id' | 'if_index' | 'type';
export type ARPEntryTypeFilter = 'all' | 'unknown' | 'static' | 'dynamic';

export interface PONAvailability {
  available: boolean;
  enabled: boolean | null;
  reason: 'ready' | 'disabled' | 'schedule_missing' | 'schedule_unavailable'
    | 'runner_missing' | 'runner_unwired' | 'dependency_unknown' | 'dependency_unavailable';
}

// Новый строгий контракт: false/null не заменяются defaults или legacy aliases.
// В ответ не переносим extra fields; ошибки не содержат исходный payload.
function decodePONAvailability(value: unknown): PONAvailability {
  const invalidMessage = i18n.t('devices:form.pon_polling.invalid_response');
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(invalidMessage);
  }
  const { available, enabled, reason } = value as Record<string, unknown>;
  if (typeof available !== 'boolean' || (enabled !== null && typeof enabled !== 'boolean')) {
    throw new Error(invalidMessage);
  }
  switch (reason) {
    case 'ready':
      if (available && enabled === true) return { available, enabled, reason };
      break;
    case 'disabled':
      if (available && enabled === false) return { available, enabled, reason };
      break;
    case 'schedule_missing':
    case 'schedule_unavailable':
      if (!available && enabled === null) return { available, enabled, reason };
      break;
    case 'runner_missing':
    case 'runner_unwired':
    case 'dependency_unknown':
    case 'dependency_unavailable':
      if (!available) return { available, enabled, reason };
      break;
  }
  throw new Error(invalidMessage);
}

export interface ARPPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface ARPReadFacadeV1Item {
  ip_address: string;
  mac_address: string;
  vlan_id: number | null;
  if_index: number | null;
  type: 'unknown' | 'static' | 'dynamic';
  owner_customer_id?: number;
  owner_customer_name?: string;
  owner_device_id?: number;
  owner_device_name?: string;
  owner_lookup_checked?: boolean;
}

export interface ARPReadFacadeV1Meta {
  source: ARPReadSource;
  provenance: ARPProvenance;
  freshness: ARPFreshness;
  completeness: 'unknown';
}

export interface ARPReadFacadeV1Response {
  items: ARPReadFacadeV1Item[];
  pagination: ARPPagination;
  meta: ARPReadFacadeV1Meta;
}

export type ARPCapabilityV1Reason =
  | 'available' | 'feature_disabled' | 'feature_unavailable' | 'legacy_source' | 'v5_owned_source'
  | 'snmp_disabled' | 'hardware_unsupported' | 'no_address' | 'target_denied'
  | 'target_check_deferred';

export interface ARPCapabilityV1Response {
  available: boolean;
  reason: ARPCapabilityV1Reason;
}

export interface ARPReadFacadeV1Query {
  filters?: {
    ip?: string;
    mac?: string;
    vlan_id?: number;
    if_index?: number;
    type?: ARPEntryTypeFilter;
  };
  sort_by?: ARPOrderField;
  sort_desc?: boolean;
  signal?: AbortSignal;
}

export interface PollDeviceRequest {
  task_name: string; // "IfList", "IfTraffic", "IfErrors", "FdbTable", "ArpTable", etc.
  priority?: number; // 0-9, default 5
  onus?: string[]; // Список ONU (SN/MAC) для таргетированных задач
  force?: boolean;
  interfaces?: number[];
  profile_id?: number;
  schedule_at?: string;
  note?: string;
  // Расширение для массовых действий без передачи всех ключей
  scope?: 'selection' | 'all';
  filters?: Record<string, unknown>;
}

interface PollDeviceRawResponse {
  data: {
    taskId: string;
    status: string;
  };
  meta: {
    message?: Record<string, string>;
    createdAt?: string;
  };
}

export interface PollDeviceResponse {
  taskId: string;
  status: string;
  message: string;
  createdAt: string;
}

interface DevicePonPortsRawResponse {
  device_id: number;
  ports?: Array<{
    olt_if_index?: number | null;
    oltIfIndex?: number | null;
    iface_name?: string | null;
    ifaceName?: string | null;
    total?: number;
    online?: number;
    offline?: number;
    unknown?: number;
    bad_level?: number;
    badLevel?: number;
    last_seen?: string | null;
    lastSeen?: string | null;
  }>;
  summary?: {
    ports?: number | null;
    total_onu?: number | null;
    totalOnu?: number | null;
    online_onu?: number | null;
    onlineOnu?: number | null;
    offline_onu?: number | null;
    offlineOnu?: number | null;
    unknown_onu?: number | null;
    unknownOnu?: number | null;
    bad_level_onu?: number | null;
    badLevelOnu?: number | null;
    updated_at?: string | null;
    updatedAt?: string | null;
  };
}

// Cable Test
export interface StartCableTestRequest {
  ports?: number[]; // Если пусто - тестировать все порты
  priority?: number;
}

export interface StartCableTestResponse {
  deviceId: number;
  dateAdd: number;
  status: string;
  message: string;
  createdAt: string;
}

export interface CableTestHistoryItem {
  deviceId: number;
  deviceName?: string;
  dateAdd: number;
  isFinish: boolean;
  results?: CableTestPortResult[];
  createdAt: string;
}

export interface CableTestPairResult {
  pair?: number;
  length?: number;
  status?: string;
}

export interface CableTestPortResult {
  port: number;
  length: number; // Длина кабеля в метрах
  status: string; // "ok", "open", "short", "error"
  pairs?: CableTestPairResult[];
}

export interface CableTestResultResponse {
  deviceId: number;
  deviceName?: string;
  dateAdd: number;
  isFinish: boolean;
  createdAt: string;
  results: CableTestPortResult[];
}

export interface CableTestHistoryResponse {
  items: CableTestHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export type InterfaceAdminAction = 'up' | 'down';

export interface ControlInterfaceResponse {
  success: boolean;
  message: string;
  taskId: string;
}

export interface PollerInterfacePayload {
  index: number;
  if_index?: number;
  name?: string;
  description?: string;
  admin_status?: string;
  oper_status?: string;
  speed?: number | string;
  in_octets?: number | string;
  out_octets?: number | string;
  in_errors?: number | string;
  out_errors?: number | string;
  last_change?: number | string;
  phys_address?: string;
  mtu?: number | string;
  type?: number | string;
  in_discards?: number | string;
  out_discards?: number | string;
  in_ucast_pkts?: number | string;
  out_ucast_pkts?: number | string;
}

export interface PollerTempliteCounters {
  total?: number;
  up?: number;
  down?: number;
  with_errors?: number;
}

export interface PollerTempliteDerived {
  up?: number[];
  down?: number[];
  descriptions?: Record<string, string>;
  counters?: PollerTempliteCounters;
}

export interface PollerTempliteIfListEntry {
  if_index: number;
  name?: string;
  description?: string;
  alias?: string;
  status_admin?: number;
  status_oper?: number;
  type?: number;
  speed?: number;
  ifHighSpeed?: number;
  ifMtu?: number;
  ifPhysAddress?: string;
  ifLastChange?: number;
  in_octets?: number;
  out_octets?: number;
  ifInErrors?: number;
  ifOutErrors?: number;
  ifInDiscards?: number;
  ifOutDiscards?: number;
}

export interface PollerTempliteIfTrafficEntry {
  if_index: number;
  in_octets?: number;
  out_octets?: number;
}

export interface PollerTempliteIfErrorsEntry {
  if_index: number;
  in_errors?: number;
  out_errors?: number;
}

export interface PollerTempliteTables {
  if_list?: PollerTempliteIfListEntry[];
  if_traffic?: PollerTempliteIfTrafficEntry[];
  if_errors?: PollerTempliteIfErrorsEntry[];
}

export interface PollerTempliteSnapshot {
  tables?: PollerTempliteTables;
  derived?: PollerTempliteDerived;
}

export interface PollerIfacesCacheResponse {
  data: {
    task: string;
    payload: PollerPayload;
  };
  meta: {
    generatedAt: string;
  };
}

export interface PollerIfacesSyncResponse {
  data: {
    taskId: string;
    status: string;
    result: PollerPayload;
  };
  meta: {
    source: string;
    durationMs?: number;
    startedAt?: string;
    finishedAt?: string;
  };
}

export interface PollerOnuMeasurement {
  onu_id?: string;
  onu?: {
    olt_if_index?: number;
    nr?: number;
    unique_id?: string;
  };
  value?: number | string | boolean | null;
  last_update?: string;
  source?: string;
}

export interface PollerOnuInfo {
  onu_id?: string;
  olt_if_index?: number;
  nr?: number;
  unique_id?: string;
  name?: string;
  type?: string;
  serial?: string;
  mac?: string;
  vendor?: string;
  model?: string;
  firmware?: string;
  hardware?: string;
  description?: string;
  sort_key?: number;
}

export interface PollerPonInterface {
  onu_id?: string;
  number?: number;
  admin_status?: string;
  oper_status?: string;
  port_speed_mbit?: number;
  rx_octets?: number | string;
  tx_octets?: number | string;
  rx_errors?: number | string;
  tx_errors?: number | string;
}

export interface PollerPonDownCause {
  onu_id?: string;
  onu?: {
    olt_if_index?: number;
    nr?: number;
    unique_id?: string;
  };
  type?: string;
  value?: number | string | boolean;
}

export interface PollerPonResult {
  onus?: PollerOnuInfo[];
  onu_rx_power?: PollerOnuMeasurement[];
  onu_tx_power?: PollerOnuMeasurement[];
  temperature?: PollerOnuMeasurement[];
  distance?: PollerOnuMeasurement[];
  state?: PollerOnuMeasurement[];
  dereg_reasons?: PollerPonDownCause[];
  interfaces?: PollerPonInterface[];
  olt_rx_power?: PollerOnuMeasurement[];
  olt_tx_power?: PollerOnuMeasurement[];
}

export interface PollerVlanEntry {
  vlan_id: number;
  name?: string;
  status?: string;
  ports?: number[];
}

// REST /devices/:id/vlan/paged keeps `ports` as the legacy union. The role
// arrays are additive and optional in the type for rolling backend upgrades.
export interface DeviceVlanPagedItem {
  vlan_id: number;
  name?: string;
  status?: string;
  ports?: number[];
  tagged_if_indexes?: number[];
  untagged_if_indexes?: number[];
}

export interface PollerFdbEntry {
  mac_address: string;
  vlan_id?: number;
  port?: number;
  port_name?: string;
  type?: string;
  date_first?: number;
  date_last?: number;
}

export interface PollerArpEntry {
  ip_address?: string;
  mac_address?: string;
  vlan_id?: number;
  if_index?: number;
  type?: string;
}

export interface PollerPayload {
  interfaces?: PollerInterfacePayload[];
  templite?: PollerTempliteSnapshot;
  pon?: PollerPonResult;
  vlans?: PollerVlanEntry[];
  fdb?: PollerFdbEntry[];
  arp?: PollerArpEntry[];
  started_at?: string;
  finished_at?: string;
}

export interface PollerPonCacheResponse {
  data: {
    task: string;
    payload: PollerPayload;
  };
  meta: {
    generatedAt: string;
  };
}

export interface DevicePonMetricsResponse {
  data: {
    pon?: PollerPonResult;
  };
  meta: {
    measurements?: Record<string, string>;
    latest?: string;
  };
}

export interface PollerVlanCacheResponse {
  data: {
    task: string;
    payload: PollerPayload;
  };
  meta: {
    generatedAt: string;
  };
}

export interface PollerFdbCacheResponse {
  data: {
    task: string;
    payload: PollerPayload;
  };
  meta: {
    generatedAt: string;
  };
}

export interface PollerArpCacheResponse {
  data: {
    task: string;
    payload: PollerPayload;
  };
  meta: {
    generatedAt: string;
  };
}

type PollerCacheResponseBase = {
  data: {
    task: string;
    payload: PollerPayload;
  };
  meta: {
    generatedAt: string;
  };
};

const createEmptyPollerCacheResponse = (task: string): PollerCacheResponseBase => ({
  data: {
    task,
    payload: {},
  },
  meta: {
    generatedAt: new Date().toISOString(),
  },
});

const withStatusFallback = async <T>(
  request: () => Promise<T>,
  fallback: () => T | Promise<T>,
  statuses: number[],
): Promise<T> => {
  try {
    return await request();
  } catch (error) {
    if (isAxiosError(error) && statuses.includes(error.response?.status ?? 0)) {
      return await fallback();
    }
    throw error;
  }
};

const withNotFoundFallback = <T>(request: () => Promise<T>, fallback: () => T | Promise<T>) =>
  withStatusFallback(request, fallback, [404]);

const withForbiddenFallback = <T>(request: () => Promise<T>, fallback: () => T) =>
  withStatusFallback(request, fallback, [403]);

// Специальная функция для обработки ошибок с информативными сообщениями
const handleApiError = (error: unknown): never => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;

    if (status === 403) {
      throw new Error('devices:errors.onu_forbidden');
    }
    if (status === 404) {
      throw new Error('devices:errors.pon_not_found');
    }
    if (status === 500) {
      throw new Error('devices:errors.server');
    }

    throw new Error(message || 'devices:errors.load_failed');
  }

  throw error instanceof Error ? error : new Error('devices:interfaces.actions.status.sync.error_unknown');
};

const createEmptyPonPortStatsResponse = (deviceId: number): DevicePonPortStatsResponse => ({
  deviceId,
  ports: [],
  summary: {
    ports: 0,
    totalOnu: 0,
    onlineOnu: 0,
    offlineOnu: 0,
    unknownOnu: 0,
    badLevelOnu: 0,
    updatedAt: new Date().toISOString(),
  },
});

const createEmptyOnuListResponse = (filter: { page: number; limit: number }): ONUListResponse => ({
  items: [],
  pagination: {
    page: filter.page,
    limit: filter.limit,
    total: 0,
    total_pages: 0,
  },
  statistics: {
    total: 0,
    online: 0,
    offline: 0,
    unknown: 0,
    bad_level: 0,
  },
});

export interface StartPollerIfacesResponse {
  data: {
    taskId: string;
    status: string;
  };
  meta: {
    message: Record<string, string>;
    createdAt: string;
  };
}

export interface UpdateDeviceObserverPayload {
  profileId?: number | null;
  isPolling: boolean;
  isAlert?: boolean;
}

const normalizeCableStatus = (status?: string): string => {
  if (!status) {
    return '';
  }

  const lowered = status.toLowerCase();
  if (lowered.startsWith('cable_status_')) {
    return lowered.replace('cable_status_', '');
  }

  return lowered;
};

const normalizeCablePortResult = (result: CableTestPortResult): CableTestPortResult => ({
  ...result,
  status: normalizeCableStatus(result.status),
  pairs: result.pairs?.map((pair) => ({
    ...pair,
    status: normalizeCableStatus(pair.status),
  })),
});

// PON (ONU)
export interface ONUListItem {
  id: number;
  device_id: number;
  olt_if_index?: number;
  nr?: number;
  unique_id?: string;
  sn?: string;
  sn_second?: string;
  mac?: string;
  vendor?: string;
  model?: string;
  firmware?: string;
  hardware?: string;
  description?: string;
  iface_name?: string;
  distance?: number;
  level_onu_rx?: number;
  level_onu_tx?: number;
  level_olt_rx?: number;
  level_olt_tx?: number;
  is_online: boolean;
  is_unknown: boolean;
  is_level_bad: boolean;
  /** ⚠️ ДВА состояния: "good" | "bad" — как у эталона (`Olt.php:434-435`). Ярус "warning"
   *  сервер больше не производит (ц.266): он включался только при непустых колонках
   *  `level_min`/`level_max`, а они пусты во всех строках клона. */
  level_status: string;
  reason_offline?: string;
  updated_at: string;
  is_syncing?: boolean; // Флаг синхронизации (модуль wnm_pon запущен и данные еще обновляются)
  onu_device_id?: number;
  pon_rx_power?: number;
  pon_tx_power?: number;
  pon_state?: string;
  pon_updated_at?: string;
  pon_temperature?: number;
  // Информация о владельце (из JOIN к customers)
  owner_customer_id?: number;
  owner_name?: string;
  owner_username?: string;
  owner_balance?: number;
  owner_building?: string;
  owner_apartment?: string;
  owner_phone?: string;
  owner_device_name?: string;
}

export interface ONUStatistics {
  total: number;
  online: number;
  offline: number;
  unknown: number;
  bad_level: number;
}

export interface ONUListResponse {
  items: ONUListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  statistics?: ONUStatistics;
}

export interface ONUMetricsResponse {
  level_onu_rx?: number;
  level_onu_tx?: number;
  level_olt_rx?: number;
  level_olt_tx?: number;
  is_online: boolean;
  is_level_bad: boolean;
  level_status: string;
  reason_offline?: string;
  updated_at: string;
}

// LIVE метрики ONU через прямой SNMP запрос к оборудованию
export interface ONULiveMetricsResponse {
  level_onu_rx: number | null;
  level_onu_tx: number | null;
  level_olt_rx: number | null;
  level_olt_tx: number | null;
  distance: number | null;
  is_online: boolean;
  is_level_bad: boolean;
  level_status: string;
  updated_at: string;
  source: 'snmp' | 'db'; // Источник данных: прямой SNMP или БД (fallback)
  fallback_reason?: string;
}

export interface ONUDetailsResponse {
  id: number;
  device_id: number;
  device_name: string;
  olt_if_index?: number;
  nr?: number;
  unique_id?: string;
  sn?: string;
  sn_second?: string;
  mac?: string;
  vendor?: string;
  model?: string;
  firmware?: string;
  hardware?: string;
  description?: string;
  iface_name?: string;
  iface_state?: number;
  level_onu_rx?: number;
  level_onu_tx?: number;
  level_olt_rx?: number;
  level_olt_tx?: number;
  level_min?: number;
  level_max?: number;
  is_level_bad: boolean;
  level_status: string;
  distance?: number;
  is_online: boolean;
  is_unknown: boolean;
  reason_offline?: string;
  reason_offline_type?: string;
  onu_device_id?: number;
  onu_inventory_id?: number;
  updated_at: string;
  // Информация о владельце
  owner_customer_id?: number;
  owner_name?: string;
  owner_username?: string;
  owner_balance?: number;
  owner_building?: string;
  owner_apartment?: string;
  owner_phone?: string;
  owner_device_name?: string;
  // Дополнительная информация
  device_ip?: string;
  device_mac?: string;
  warehouse_cost?: number;
  inventory_number?: string;
  install_date?: string;
  device_notes?: string;
  ifaceCount?: number; // Количество портов ONU
  portCountFree?: number;
  portCountBad?: number;
}

export interface ONUFilter {
  olt_if_index?: number;
  query?: string;
  is_online?: boolean;
  is_unknown?: boolean;
  is_level_bad?: boolean;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_desc?: boolean;
}

export interface ONULevelHistoryItem {
  device_id: number;
  unique_id?: string;
  onu_name: string;
  level_onu_rx: number; // dBm
  date_from: number; // Unix timestamp
  date_to: number; // Unix timestamp
  nr?: number;
  olt_if_index?: number;
  created_at: string; // ISO 8601
  updated_at: string; // ISO 8601
  /** ⚠️ Вердикт эталона по ЭТОМУ замеру (`Olt.php:434-435`), считает СЕРВЕР (ц.265).
   *  На клиенте правило было неполным: границы нестрогие и без исключения для нулевого
   *  уровня — 4276 замеров у 193 ONU красились «слишком сильными» при «нет данных». */
  is_bad?: boolean;
}

/** Полоса допустимого уровня сигнала, как её настроил ОПЕРАТОР (ц.264).
 *  Считает сервер: пороги живут в настройках установки (`DBM_MIN`/`DBM_MAX`) и в свойствах
 *  устройства (`dbm_all_*`), которых у клиента нет. Эталон: `Olt.php:375-420`. */
export interface PonLevelBand {
  /** Ниже — плохо. */
  min: number;
  /** Выше — тоже плохо (слишком сильный сигнал); до ц.264 клиент верхнюю границу не проверял. */
  max: number;
}

export interface ONULevelHistoryResponse {
  items: ONULevelHistoryItem[];
  total: number;
  band?: PonLevelBand;
  /** Считает ли оператор нулевой уровень плохим (`DEVICE_IS_PON_0_IS_BAD`).
   *  Нужен экрану не для пересчёта вердикта — он приходит построчно, — а чтобы объяснить,
   *  почему нулевые замеры не красные. */
  zero_is_bad?: boolean;
}

// API методы
const normalizeNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const normalizeString = (value: unknown, { trim = true }: { trim?: boolean } = {}): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  if (!trim) {
    return value;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
};

const normalizeSfpDefault = (raw: unknown): DeviceSfpDefault | undefined => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const item = raw as Record<string, unknown>;
  if (!Object.prototype.hasOwnProperty.call(item, 'state')) return undefined;
  const hasValue = Object.prototype.hasOwnProperty.call(item, 'value');
  if (item.state === 'ready' && hasValue && typeof item.value === 'number' && Number.isFinite(item.value)) {
    return { state: 'ready', value: item.value };
  }
  if (item.state === 'unconfigured' && hasValue && item.value === 0) {
    return { state: 'unconfigured', value: item.value };
  }
  if ((item.state === 'invalid' || item.state === 'unavailable') && !hasValue) {
    return { state: item.state };
  }
  return undefined;
};

const normalizeBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
      return false;
    }
  }
  return undefined;
};

const ensureBoolean = (value: unknown, fallback: boolean): boolean => {
  const normalized = normalizeBoolean(value);
  return normalized ?? fallback;
};

const normalizeIpAddresses = (value: unknown): DeviceIPAddress[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const result: DeviceIPAddress[] = [];
  value.forEach((entry) => {
    if (!entry || typeof entry !== 'object') {
      return;
    }
    const record = entry as Record<string, unknown>;
    const ip = normalizeString(record.ip);
    if (!ip) {
      return;
    }
    const mac = normalizeString(record.mac);
    result.push({
      ip,
      mac: mac || undefined,
    });
  });
  return result.length > 0 ? result : undefined;
};

const normalizeDeviceFastLinks = (value: unknown): DeviceFastLink[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const allowedProtocols = new Set(['http:', 'https:', 'telnet:', 'ssh:']);
  const result: DeviceFastLink[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    const label = normalizeString(record.label);
    const url = normalizeString(record.url);
    if (!label || !url || /\[(?:ip|id)\]/i.test(url)) continue;
    try {
      const parsed = new URL(url);
      if (!allowedProtocols.has(parsed.protocol) || parsed.username || parsed.password) continue;
    } catch {
      continue;
    }
    result.push({ label, url });
  }
  return result.length > 0 ? result : undefined;
};

// Экспортируется для гейта `__tests__/hostnameRawNotDisplayFallback.test.ts`: инвариант
// «показовое падение hostname→name не течёт в запись» проверяется на самом нормализаторе,
// а не на его копии в тесте.
export // ⚠️⚠️ ЧИТАТЬ ОБА НАПИСАНИЯ. Сервер шлёт эти поля ТОЛЬКО camelCase
// (`domain/device.go:43-46`), а нормализатор читал только snake_case — и `base` здесь НЕ
// запаска, это тот же `item` под другим типом (`base = item as Partial<Device>`), то есть
// обе ветки спрашивали один и тот же отсутствующий ключ. Следствие было не потерей данных
// (undefined фронт не отправляет, сервер не обновляет поле при nil), а ЛОЖЬЮ ЭКРАНА: форма
// правки показывала пусто у устройства с настроенным SNMP v3. Замерено ц.249.
const normalizeDeviceItem = (raw: unknown): Device => {
  const item = (typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}) as Record<string, unknown>;
  const base = item as Partial<Device>;

  const id =
    normalizeNumber(item.id) ??
    normalizeNumber(item.device_id) ??
    normalizeNumber(base.id) ??
    0;

  const name =
    normalizeString(item.name, { trim: false }) ??
    normalizeString(item.device_name, { trim: false }) ??
    normalizeString(base.name, { trim: false }) ??
    '';

  // ⚠️⚠️ `hostname` ЗДЕСЬ — ЗНАЧЕНИЕ ДЛЯ ПОКАЗА: когда его нет, падаем на имя, чтобы в
  // списках и на карточке не было пустой ячейки. В путь ЗАПИСИ это значение попадать не
  // должно — иначе форма правки префилит придуманный hostname и «Сохранить» БЕЗ единой
  // правки записывает его в БД. Замерено живьём (ц.248, устройство 84): до сохранения
  // hostname в ответе отсутствовал, после нажатия «Сохранить» стал равен имени.
  //
  // Поэтому сырое значение отдаётся ОТДЕЛЬНЫМ полем `hostname_raw`, а показовое остаётся
  // как было: 57 обращений к `.hostname` в 23 файлах не тронуты — починка аддитивная.
  const hostnameRaw =
    normalizeString(item.hostname, { trim: false }) ??
    normalizeString(item.hostName, { trim: false }) ??
    normalizeString(base.hostname, { trim: false });
  const hostname = hostnameRaw ?? name;

  const inventoryId =
    normalizeNumber(item.inventory_id) ??
    normalizeNumber(item.inventoryId) ??
    normalizeNumber(base.inventory_id) ??
    0;

  const inventoryAssortmentId =
    normalizeNumber(item.inventory_assortment_id) ??
    normalizeNumber(item.inventoryAssortmentId) ??
    normalizeNumber(base.inventory_assortment_id) ??
    undefined;

  const inventoryNumber =
    normalizeString(item.inventory_number) ??
    normalizeString(item.inventoryNumber) ??
    normalizeString(base.inventory_number);
  const serialNumber =
    normalizeString(item.serial_number) ??
    normalizeString(item.serialNumber) ??
    normalizeString(base.serial_number);
  const inventoryBarcode =
    normalizeString(item.inventory_barcode) ??
    normalizeString(item.inventoryBarcode) ??
    normalizeString(base.inventory_barcode);

  const inventoryType =
    normalizeNumber(item.inventory_section_type_id) ??
    normalizeNumber(item.inventorySectionTypeId) ??
    base.inventory_section_type_id ??
    0;

  const deviceType =
    normalizeString(item.device_type) ??
    normalizeString(item.deviceType) ??
    normalizeString(base.device_type);

  const comment =
    normalizeString(item.comment) ??
    normalizeString(item.device_comment) ??
    normalizeString(base.comment);

  const location =
    normalizeString(item.location) ??
    normalizeString(item.place) ??
    normalizeString(base.location);

  const ipAddresses =
    normalizeIpAddresses(item.ip_addresses ?? item.ipAddresses) ??
    (Array.isArray(base.ip_addresses) ? base.ip_addresses : undefined);

  const primaryIp =
    normalizeString(item.primary_ip) ??
    normalizeString(item.primaryIp) ??
    (ipAddresses && ipAddresses.length > 0 ? ipAddresses[0].ip : undefined);
  const ipField =
    normalizeString(item.ip) ??
    normalizeString((item as Record<string, unknown>).ip_address) ??
    primaryIp;

  const ifaceCount =
    normalizeNumber(item.ifaceCount) ??
    normalizeNumber(item.ifaceCount) ??
    normalizeNumber(base.ifaceCount) ??
    0;

  const isOnline =
    normalizeBoolean(item.is_online) ??
    normalizeBoolean(item.isOnline) ??
    normalizeBoolean(base.is_online);

  const customerRecord =
    typeof item.customer === 'object' && item.customer !== null
      ? (item.customer as Record<string, unknown>)
      : undefined;

  const customerId =
    normalizeNumber(item.customer_id) ??
    normalizeNumber(item.customerId) ??
    normalizeNumber(customerRecord?.id) ??
    normalizeNumber(base.customer_id);

  const customerName =
    normalizeString(item.customer_name) ??
    normalizeString(item.customerName) ??
    normalizeString(customerRecord?.name) ??
    normalizeString(customerRecord?.fullname) ??
    normalizeString(base.customer_name);

  const nodeRecord =
    typeof item.node === 'object' && item.node !== null
      ? (item.node as Record<string, unknown>)
      : undefined;

  const nodeId =
    normalizeNumber(item.node_id) ??
    normalizeNumber(item.nodeId) ??
    normalizeNumber(nodeRecord?.id) ??
    normalizeNumber(base.node_id);

  const nodeName =
    normalizeString(item.node_name) ??
    normalizeString(item.nodeName) ??
    normalizeString(nodeRecord?.name) ??
    normalizeString(nodeRecord?.fullname) ??
    normalizeString(base.node_name);

// ⚠️ Эти восемь полей сервер шлёт ТОЛЬКО camelCase (`domain/device.go:34-52`), поэтому
// читаются ОБА написания — как у `hostname` и `snmpV3SecLevel`. Без camelCase форма правки
// показывала пусто у настроенного устройства, а по секретам — пусто вместо маски `****`,
// которую отдаёт сервер с ц.252. Замерено ц.253: camelCase-чтений было 0 у всех восьми.
// ⚠️ `base` здесь НЕ запаска, а тот же объект под другим типом (`base = item as
// Partial<Device>`) — обе ветки `item.X ?? base.X` спрашивают один ключ.
  const snmpCommunity = normalizeString(item.snmp_community) ?? normalizeString(item.snmpCommunity) ?? normalizeString(base.snmp_community);
  const snmpPort = normalizeNumber(item.snmp_port) ?? normalizeNumber(item.snmpPort) ?? normalizeNumber(base.snmp_port);
  const deviceSnmpProfileId = normalizeNumber(item.device_snmp_profile_id) ?? normalizeNumber(item.deviceSnmpProfileId) ?? normalizeNumber(base.device_snmp_profile_id);

  const snmpV3SecurityName =
    normalizeString(item.snmp_v3_security_name) ?? normalizeString(item.snmpV3SecurityName) ??
    normalizeString(base.snmp_v3_security_name);
  // Сервер отдаёт `snmpProto` (тег DTO), но читаем ОБА написания — ровно тот класс, что
  // ц.253 закрыл на восьми полях: нормализатор читал только snake, а провод шлёт camelCase.
  const snmpProto = normalizeString(item.snmp_proto) ??
    normalizeString(item.snmpProto) ?? normalizeString(base.snmp_proto);
  const snmpV3SecLevel = normalizeString(item.snmp_v3_sec_level) ??
    normalizeString(item.snmpV3SecLevel) ?? normalizeString(base.snmp_v3_sec_level);
  const snmpV3AuthProtocol =
    normalizeString(item.snmp_v3_auth_protocol) ??
    normalizeString(item.snmpV3AuthProtocol) ??
    normalizeString(base.snmp_v3_auth_protocol);
  const snmpV3AuthPassphrase =
    normalizeString(item.snmp_v3_auth_passphrase) ?? normalizeString(item.snmpV3AuthPassphrase) ??
    normalizeString(base.snmp_v3_auth_passphrase);
  const snmpV3PrivProtocol =
    normalizeString(item.snmp_v3_priv_protocol) ??
    normalizeString(item.snmpV3PrivProtocol) ??
    normalizeString(base.snmp_v3_priv_protocol);
  const snmpV3PrivPassphrase =
    normalizeString(item.snmp_v3_priv_passphrase) ?? normalizeString(item.snmpV3PrivPassphrase) ??
    normalizeString(base.snmp_v3_priv_passphrase);

  const telnetUsername = normalizeString(item.telnet_username) ?? normalizeString(item.telnetUsername) ?? normalizeString(base.telnet_username);
  const telnetPassword = normalizeString(item.telnet_password) ?? normalizeString(item.telnetPassword) ?? normalizeString(base.telnet_password);
  const telnetEnablePassword =
    normalizeString(item.telnet_enable_password) ??
    normalizeString(base.telnet_enable_password);
  const telnetPort = normalizeNumber(item.telnet_port) ?? normalizeNumber(item.telnetPort) ?? normalizeNumber(base.telnet_port);
  const rawFdbMethod = Object.prototype.hasOwnProperty.call(item, 'fdbMethod') ? item.fdbMethod : item.fdb_method;
  const fdbMethod = rawFdbMethod === null || typeof rawFdbMethod === 'string' ? rawFdbMethod : undefined;
  const fdbMethodAvailable = typeof item.fdbMethodAvailable === 'boolean' ? item.fdbMethodAvailable :
    typeof item.fdb_method_available === 'boolean' ? item.fdb_method_available : undefined;
  const rawInheritedFDBMethod = item.fdbMethodInheritedEffective ?? item.fdb_method_inherited_effective;
  const fdbMethodInheritedEffective = rawInheritedFDBMethod === 'snmp' || rawInheritedFDBMethod === 'telnet'
    ? rawInheritedFDBMethod : 'unknown';

  const firmwareVersion = normalizeString(item.firmware_version) ?? normalizeString(base.firmware_version);
  const equipmentBaseId = normalizeNumber(item.equipment_base_id) ?? normalizeNumber(base.equipment_base_id);
  // ✅ МИГРАЦИЯ: провод отдаёт camelCase (`additionalComment`), нормализатор читал только
  // snake_case — поле молча терялось у ВСЕХ потребителей нормализованного устройства.
  // Найдено ц.224 карточкой заметок: в БД текст есть, на экране пусто.
  const additionalComment =
    normalizeString(item.additional_comment ?? item.additionalComment) ??
    normalizeString(base.additional_comment);
  // ✅ МИГРАЦИЯ: Поддерживаем оба формата (snake_case и camelCase) для обратной совместимости
  const uplinkIfaceList = normalizeString(item.uplink_iface_list ?? item.uplinkIfaceList, { trim: false }) ?? base.uplink_iface_list;
  const downlinkIfaceList = normalizeString(item.downlink_iface_list ?? item.downlinkIfaceList, { trim: false }) ?? base.downlink_iface_list;
  // ✅ НОРМАЛИЗАЦИЯ: Обрабатываем случай, когда приходит строка 'null' вместо null или пустой строки
  let customIfaceList = normalizeString(item.custom_iface_list ?? item.customIfaceList, { trim: false }) ?? base.custom_iface_list;
  if (customIfaceList === 'null' || customIfaceList?.trim() === 'null') {
    customIfaceList = undefined;
  }
  const sfpAttList = normalizeString(item.sfp_att_list, { trim: false }) ??
    normalizeString(item.sfpAttList, { trim: false });
  const rotationScheme = normalizeString(item.rotation_scheme) ?? normalizeString(base.rotation_scheme);

  const rawProperties = (item.properties ?? base.properties) as unknown;
  let properties: Record<string, unknown> | null | undefined;
  if (rawProperties === null) {
    properties = null;
  } else if (typeof rawProperties === 'string') {
    try {
      properties = JSON.parse(rawProperties) as Record<string, unknown>;
    } catch {
      properties = undefined;
    }
  } else if (typeof rawProperties === 'object' && rawProperties !== undefined) {
    properties = rawProperties as Record<string, unknown>;
  }

  const isSnmp = ensureBoolean(item.isSnmp ?? item.isSnmp ?? base.isSnmp, false);
  // This is a server-computed detail projection. Do not infer it from a list
  // placeholder or coerce client-shaped strings into permission to request SNMP.
  const snmpInfoAvailable = item.snmpInfoAvailable === true;
  const isPlanned = ensureBoolean(item.is_planned ?? item.isPlanned ?? base.is_planned, false);
  // ⚠️ Дефолт `false`, а не `true`: отсутствие поля означает «сервер не сказал рисовать».
  // У эталона отсутствующая настройка тоже даёт 0 (`SettingSystem.php:121-123`), то есть
  // умолчание «выключено» — паритет. Дефолт `true` нарисовал бы диагностику на устройстве
  // без IP и на планируемом, где пинговать нечего.
  const pingCardEnabled = ensureBoolean(item.pingCardEnabled ?? base.pingCardEnabled, false);
  const fastLinks = normalizeDeviceFastLinks(item.fastLinks ?? base.fastLinks);
  const isPeleng = ensureBoolean(item.is_peleng ?? item.isPeleng ?? base.is_peleng, false);
  // Блок «Опрос модулем» приходит ГОТОВЫМ: оба гейта эталона применены на сервере.
  const pollingModules = Array.isArray(item.pollingModules)
    ? (item.pollingModules as Device['polling_modules'])
    : base.polling_modules;
  const isNotificationBySms = ensureBoolean(item.is_notification_by_sms ?? base.is_notification_by_sms, false);
  const isSmsNotificationSend = ensureBoolean(item.is_sms_notification_send ?? base.is_sms_notification_send, false);
  const isNotificationByMail = ensureBoolean(item.is_notification_by_mail ?? base.is_notification_by_mail, false);
  const isMailNotificationSend = ensureBoolean(item.is_mail_notification_send ?? base.is_mail_notification_send, false);

  // notification_employees - список сотрудников для оповещения с каналами
  const notificationEmployees =
    (item.notification_employees ?? item.notificationEmployees ?? base.notification_employees) as
    | NotificationEmployee[]
    | undefined;

  const dateAdd = normalizeNumber(item.date_add) ?? normalizeNumber(item.dateAdd) ?? base.date_add;
  const createdAt = normalizeString(item.created_at, { trim: false }) ?? normalizeString(base.created_at, { trim: false });
  const updatedAt = normalizeString(item.updated_at, { trim: false }) ?? normalizeString(base.updated_at, { trim: false });
  const lastActivityUnix =
    normalizeNumber(item.last_activity_unix) ??
    normalizeNumber(item.lastActivityUnix) ??
    base.last_activity_unix;
  const lastActivity =
    normalizeString(item.last_activity) ??
    normalizeString(item.lastActivity) ??
    normalizeString(base.last_activity);

  const normalized: Device = {
    id,
    inventory_id: inventoryId,
    inventory_assortment_id: inventoryAssortmentId,
    inventory_number: inventoryNumber,
    serial_number: serialNumber ?? undefined,
    inventory_barcode: inventoryBarcode ?? undefined,
    name,
    hostname,
    hostname_raw: hostnameRaw ?? null,
    comment,
    customer_id: customerId,
    customer_name: customerName ?? undefined,
    node_id: nodeId,
    node_name: nodeName ?? undefined,
    location,
    ifaceCount: ifaceCount,
    // ⚠️⚠️ Нормализатор собирает строку ПОЛЕМ ЗА ПОЛЕМ и молча теряет всё, что в нём не
    // названо. Ц.295 добавил колонку «Портов свободно», сервер поле отдавал, DTO его нёс,
    // гейты были зелёные — а в таблице стоял прочерк во всех строках, потому что сюда поле
    // не дописали. Поймала ЖИВАЯ проверка, не прогон: гейт колонки утверждал её ОПРЕДЕЛЕНИЕ,
    // а не путь данных.
    portCountFree: normalizeNumber(item.portCountFree) ?? normalizeNumber(base.portCountFree),
    portCountBad: normalizeNumber(item.portCountBad) ?? normalizeNumber(base.portCountBad),
    inventory_section_type_id: inventoryType,
    device_type: deviceType,
    is_online: isOnline,
    isSnmp: isSnmp,
    snmpInfoAvailable,
    // Не приводим строки/числа и не придумываем default для старого ответа.
    isPollingPon: typeof item.isPollingPon === 'boolean' ? item.isPollingPon : undefined,
    isPollingIfaceWhenOpenCard: typeof item.isPollingIfaceWhenOpenCard === 'boolean' ? item.isPollingIfaceWhenOpenCard : undefined,
    interfaceAutoPollOnCard: item.interfaceAutoPollOnCard === true,
    is_planned: isPlanned,
    pingCardEnabled,
    fastLinks,
    snmp_community: snmpCommunity,
    snmp_port: snmpPort,
    device_snmp_profile_id: deviceSnmpProfileId,
    snmp_v3_security_name: snmpV3SecurityName,
    snmp_proto: snmpProto,
    snmp_v3_sec_level: snmpV3SecLevel,
    snmp_v3_auth_protocol: snmpV3AuthProtocol,
    snmp_v3_auth_passphrase: snmpV3AuthPassphrase,
    snmp_v3_priv_protocol: snmpV3PrivProtocol,
    snmp_v3_priv_passphrase: snmpV3PrivPassphrase,
    telnet_username: telnetUsername,
    telnet_password: telnetPassword,
    telnet_enable_password: telnetEnablePassword,
    telnet_port: telnetPort,
    fdb_method: fdbMethod,
    fdb_method_available: fdbMethodAvailable,
    fdb_method_inherited_effective: fdbMethodInheritedEffective,
    firmware_version: firmwareVersion,
    // ⚠️ Угол раскрытия и форма раздела: провод camelCase, читатель один.
    // `azimut` может быть null (поля не было), и это НЕ 0 — 0 значит «угол ноль».
    azimut: (item.azimut ?? base.azimut) as number | null | undefined,
    // ⚠️ Цвет хранится БЕЗ решётки (`ee1d24`) — решётку добавляет ФОРМА, как у эталона
    // (`DeviceHtml.php:5823-5826`). Нормализатор её не трогает: иначе решётка появилась бы
    // в двух местах и одно из них уехало бы в колонку.
    color_on_map: (item.colorOnMap ?? base.color_on_map) as string | null | undefined,
    // ⚠️ Через `??`, а не `||`: `false` — осмысленное значение, и `||` схлопнул бы его в
    // значение по умолчанию. Оба поля сервер отдаёт ВСЕГДА (в домене без `omitempty`).
    is_always_on_map: (item.isAlwaysOnMap ?? base.is_always_on_map) as boolean | undefined,
    section_on_map: (item.sectionOnMap ?? base.section_on_map) as boolean | undefined,
    // ⚠️ `item` — сырое тело с провода (camelCase), `base` — уже нормализованный Device
    // (snake_case). Написание у них РАЗНОЕ, и компилятор это поймал.
    section_shape_type: (item.sectionShapeType ?? base.section_shape_type ?? 0) as number,
    equipment_base_id: equipmentBaseId,
    additional_comment: additionalComment,
    uplink_iface_list: uplinkIfaceList,
    downlink_iface_list: downlinkIfaceList,
    custom_iface_list: customIfaceList,
    sfp_att_list: sfpAttList,
    sfpDefault: normalizeSfpDefault(item.sfpDefault),
    rotation_scheme: rotationScheme,
    properties: properties ?? null,
    is_peleng: isPeleng,
    polling_modules: pollingModules,
    canTurnInterfaces: item.canTurnInterfaces === true,
    is_notification_by_sms: isNotificationBySms,
    is_sms_notification_send: isSmsNotificationSend,
    is_notification_by_mail: isNotificationByMail,
    is_mail_notification_send: isMailNotificationSend,
    date_add: dateAdd,
    created_at: createdAt,
    updated_at: updatedAt,
    last_activity: lastActivity ?? undefined,
    last_activity_unix: lastActivityUnix,
    customer: undefined,
    node: undefined,
    primary_ip: primaryIp ?? null,
    ip: ipField ?? null,
    ip_addresses: ipAddresses,
    notification_employees: notificationEmployees,
  };

  if (customerId !== undefined) {
    const referenceName = customerName ?? normalizeString(customerRecord?.fullname) ?? normalizeString(customerRecord?.name);
    if (referenceName) {
      normalized.customer = { id: customerId, name: referenceName, fullname: referenceName };
    } else {
      normalized.customer = { id: customerId, name: String(customerId) };
    }
  } else if (customerRecord) {
    const fallbackId = normalizeNumber(customerRecord.id);
    const fallbackName =
      normalizeString(customerRecord.fullname) ??
      normalizeString(customerRecord.name) ??
      normalizeString(customerRecord.login);
    if (fallbackId !== undefined && fallbackName) {
      normalized.customer = { id: fallbackId, name: fallbackName, fullname: fallbackName };
      normalized.customer_id = fallbackId;
      normalized.customer_name = fallbackName;
    }
  }

  if (nodeId !== undefined) {
    const referenceName = nodeName ?? normalizeString(nodeRecord?.fullname) ?? normalizeString(nodeRecord?.name);
    if (referenceName) {
      normalized.node = { id: nodeId, name: referenceName, fullname: referenceName };
    } else {
      normalized.node = { id: nodeId, name: String(nodeId) };
    }
  } else if (nodeRecord) {
    const fallbackId = normalizeNumber(nodeRecord.id);
    const fallbackName = normalizeString(nodeRecord.name) ?? normalizeString(nodeRecord.fullname);
    if (fallbackId !== undefined && fallbackName) {
      normalized.node = { id: fallbackId, name: fallbackName, fullname: fallbackName };
      normalized.node_id = fallbackId;
      normalized.node_name = fallbackName;
    }
  }

  if (!normalized.customer_name && normalized.customer) {
    normalized.customer_name = normalized.customer.fullname ?? normalized.customer.name;
  }
  if (!normalized.node_name && normalized.node) {
    normalized.node_name = normalized.node.fullname ?? normalized.node.name;
  }

  return normalized;
};

const normalizeDeviceListResponse = (
  data: unknown,
  fallback?: { page?: number; limit?: number },
): DeviceListResponse => {
  const raw = data as { items?: unknown[]; pagination?: Record<string, unknown> };
  const rawItems = Array.isArray(raw?.items) ? raw.items : [];

  const paginationSource = (raw?.pagination ?? {}) as Record<string, unknown>;
  const page = normalizeNumber(paginationSource.page) ?? fallback?.page ?? 1;
  const limit = normalizeNumber(paginationSource.limit) ?? fallback?.limit ?? 20;
  const total = normalizeNumber(paginationSource.total) ?? rawItems.length;
  const totalPages = normalizeNumber(paginationSource.total_pages) ?? (limit > 0 ? Math.ceil(total / limit) : 0);

  const items = rawItems.map(normalizeDeviceItem);

  // ⚠️ `stats` СЕРВЕРА обязан доехать до экрана. Раньше нормализатор собирал ответ только
  // из `items` и `pagination`, и счётчики молча терялись: тип объявляет `stats?:`
  // необязательным, поэтому пропажу не ловил ни `tsc`, ни ревью. Экран падал в запасную
  // ветку `useMemo` и считал онлайн/офлайн по ТЕКУЩЕЙ СТРАНИЦЕ — оператор на
  // «Коммутаторах» видел «Всего 1 481 / Офлайн 20», где 20 это размер страницы.
  const statsSource = (data as { stats?: Record<string, unknown> } | null)?.stats;
  const stats =
    statsSource && typeof statsSource === 'object'
      ? {
          total: normalizeNumber(statsSource.total) ?? total,
          online: normalizeNumber(statsSource.online) ?? 0,
          offline: normalizeNumber(statsSource.offline) ?? 0,
        }
      : undefined;

  return {
    items,
    pagination: {
      page: page ?? 1,
      limit: limit ?? 20,
      total,
      total_pages: totalPages ?? (limit ? Math.ceil(total / limit) : 0),
    },
    ...(stats ? { stats } : {}),
  };
};

const unwrapDeviceData = (data: unknown): unknown => {
  if (data && typeof data === 'object' && 'data' in (data as Record<string, unknown>)) {
    return (data as Record<string, unknown>).data;
  }
  return data;
};

const serializeDevicePayload = (payload: Partial<DevicePayload>): Record<string, unknown> => {
  const body: Record<string, unknown> = {};

  if (payload.inventory_id !== undefined) body.inventoryId = payload.inventory_id;
  if (payload.name !== undefined) body.name = payload.name;
  // Hostname: отправляем только если есть значение (не null и не пустая строка)
  // Если hostname пустой или null, поле не отправляется (omitempty на бэкенде)
  if (payload.hostname !== undefined && payload.hostname !== null && payload.hostname !== '') {
    body.hostname = payload.hostname;
  }
  if (payload.comment !== undefined) body.comment = payload.comment;
  // customer_id = FK абонента (эталон v4 DeviceAR), НЕ слот оповещения сотрудника.
  // В форме V5 нет пикера абонента, а бэкенд эту колонку на Update не пишет (только сохраняет как есть),
  // поэтому customer_id из формы НЕ отправляем — иначе round-trip затирал привязку абонента (5557/5681 устройств).
  // Оповещение сотрудников идёт отдельным полем notification_employees.
  // notification_employees - новое поле для нескольких сотрудников с каналами
  if (payload.notification_employees !== undefined) body.notificationEmployees = payload.notification_employees;
  if (payload.node_id !== undefined) body.nodeId = payload.node_id;
  if (payload.location !== undefined) body.location = payload.location;
  if (payload.ifaceCount !== undefined) body.ifaceCount = payload.ifaceCount;

  if (payload.isSnmp !== undefined) body.isSnmp = payload.isSnmp;
  if (typeof payload.isPollingPon === 'boolean') body.isPollingPon = payload.isPollingPon;
  if (typeof payload.isPollingIfaceWhenOpenCard === 'boolean') body.isPollingIfaceWhenOpenCard = payload.isPollingIfaceWhenOpenCard;
  if (payload.snmp_community !== undefined) body.snmpCommunity = payload.snmp_community;
  if (payload.snmp_port !== undefined) body.snmpPort = payload.snmp_port;
  if (payload.device_snmp_profile_id !== undefined) body.deviceSnmpProfileId = payload.device_snmp_profile_id;

  if (payload.snmp_v3_security_name !== undefined) body.snmpV3SecurityName = payload.snmp_v3_security_name;
  if (payload.snmp_proto !== undefined) body.snmpProto = payload.snmp_proto;
  if (payload.snmp_v3_sec_level !== undefined) body.snmpV3SecLevel = payload.snmp_v3_sec_level;
  if (payload.snmp_v3_auth_protocol !== undefined) body.snmpV3AuthProtocol = payload.snmp_v3_auth_protocol;
  if (payload.snmp_v3_auth_passphrase !== undefined) body.snmpV3AuthPassphrase = payload.snmp_v3_auth_passphrase;
  if (payload.snmp_v3_priv_protocol !== undefined) body.snmpV3PrivProtocol = payload.snmp_v3_priv_protocol;
  if (payload.snmp_v3_priv_passphrase !== undefined) body.snmpV3PrivPassphrase = payload.snmp_v3_priv_passphrase;

  if (payload.telnet_username !== undefined) body.telnetUsername = payload.telnet_username;
  if (payload.telnet_password !== undefined) body.telnetPassword = payload.telnet_password;
  if (payload.telnet_enable_password !== undefined) body.telnetEnablePassword = payload.telnet_enable_password;
  if (payload.telnet_port !== undefined) body.telnetPort = payload.telnet_port;

  if (payload.firmware_version !== undefined) body.firmwareVersion = payload.firmware_version;
  if (payload.azimut !== undefined) body.azimut = payload.azimut;
  if (payload.color_on_map !== undefined) body.colorOnMap = payload.color_on_map;
  if (payload.is_always_on_map !== undefined) body.isAlwaysOnMap = payload.is_always_on_map;
  if (payload.additional_comment !== undefined) body.additionalComment = payload.additional_comment;
  if (payload.uplink_iface_list !== undefined) body.uplinkIfaceList = payload.uplink_iface_list;
  if (payload.downlink_iface_list !== undefined) body.downlinkIfaceList = payload.downlink_iface_list;
  if (payload.custom_iface_list !== undefined) body.customIfaceList = payload.custom_iface_list;
  if (payload.sfp_att_list !== undefined) body.sfpAttList = payload.sfp_att_list;
  if (payload.rotation_scheme !== undefined) body.rotationScheme = payload.rotation_scheme;

  if (payload.properties !== undefined) {
    body.properties = payload.properties;
  }

  return body;
};

/**
 * Складская позиция, из которой можно завести устройство.
 * Форма ответа — `backend/internal/device/domain/inventory_candidate.go`.
 */
export interface InventoryCandidate {
  id: number;
  assortmentName: string;
  serialNumber?: string;
  inventoryNumber?: string;
  sectionName?: string;
  sectionTypeId: number;
  /** Число портов, объявленное МОДЕЛЬЮ (эталон подставляет его в поле «Порты»). */
  defaultIfaceCount: number;
}

/**
 * Поиск складских позиций для формы СОЗДАНИЯ устройства.
 *
 * ⚠️ Ходит в `/devices/inventory-candidates`, а НЕ в готовую `/inventory/items`, и это не
 * дублирование: `/inventory/items` закрыта правом `inventory.view`, а эталон свой список
 * кандидатов гейтит правом на УСТРОЙСТВА (`inventoryListInAccount()` не содержит ни одной
 * проверки права на инвентарь). Через `/inventory/items` оператор с `devices.create` и без
 * `inventory.view` получил бы пустой список на обязательном поле. Подробности — в
 * `inventory_candidates_handler.go`.
 */
export async function searchInventoryCandidates(
  query: string,
  limit = 20,
): Promise<InventoryCandidate[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  const response = await api.get<{ items?: InventoryCandidate[] }>(
    `/devices/inventory-candidates?query=${encodeURIComponent(trimmed)}&limit=${limit}`,
  );
  return response.data?.items ?? [];
}

/** Строка выпадающего списка «Модель» в фильтре списка устройств. */
export interface DeviceModelOption {
  id: number;
  name: string;
}

/**
 * Список моделей (позиций номенклатуры) для фильтра по модели.
 *
 * ⚠️ Ходит в `/devices/models`, а не в складскую `/inventory/references/assortments`, и это
 * ТОТ ЖЕ случай, что уже разобран выше у `searchInventoryCandidates`: складские ручки
 * закрыты правом `inventory.view` + `inventory.catalog`, а эталон строит этот `<select>`
 * прямо внутри экрана устройств, закрытого только правом на устройства. Вторая причина —
 * складская ручка принимает ОДИН раздел (`sectionId`), а эталону нужны все разделы ТИПА;
 * на клоне у типа 1 их десять, и подмена дала бы молчаливое подмножество.
 *
 * ⚠️ Поиск СЕРВЕРНЫЙ: параметр `q` уходит в запрос, а не фильтрует уже загруженное. У
 * оператора с большой номенклатурой «отфильтруй первые N» означало бы недостижимую опцию.
 */
export async function fetchDeviceModels(params: {
  inventorySectionTypeId?: number;
  inventorySectionId?: number;
  query?: string;
  limit?: number;
}): Promise<{ items: DeviceModelOption[]; total: number }> {
  const search = new URLSearchParams();
  if (params.inventorySectionTypeId !== undefined) {
    search.set('inventory_section_type_id', String(params.inventorySectionTypeId));
  }
  if (params.inventorySectionId !== undefined) {
    search.set('inventory_section_id', String(params.inventorySectionId));
  }
  const trimmed = params.query?.trim();
  if (trimmed) {
    search.set('q', trimmed);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  const response = await api.get<{ items?: DeviceModelOption[]; total?: number }>(
    `/devices/models?${search.toString()}`,
  );
  return { items: response.data?.items ?? [], total: response.data?.total ?? 0 };
}

/** Абонент, скоммутированный на порту устройства. */
export interface DevicePortCustomer {
  port: number;
  customerId: number;
  customerName: string;
  accountNumber: string;
  /** 0 — абонент на порту самого устройства; 1+ — найден обходом вниз. */
  depth: number;
  /** Имя устройства, за которым найден косвенный абонент. Пусто у прямых. */
  viaDeviceName: string;
}

/**
 * Абоненты на портах устройства.
 *
 * ⚠️ С ц.304 сюда входят и КОСВЕННЫЕ абоненты — за дочерними устройствами (обход вниз с
 * пропуском uplink-портов). НЕ входят пока абоненты за сплиттерами, кроссами и кабельными
 * линиями: на клоне таких цепочек ноль, и код для них нечем проверить живьём. Разница
 * обязана быть названа на экране, иначе список читается как полный.
 */
export async function fetchDevicePortCustomers(deviceId: number): Promise<{
  items: DevicePortCustomer[];
  total: number;
  depthCapped: boolean;
  depthCap: number;
}> {
  const response = await api.get<{
    items?: DevicePortCustomer[];
    total?: number;
    depthCapped?: boolean;
    depthCap?: number;
  }>(`/devices/${deviceId}/customers`);
  return {
    items: response.data?.items ?? [],
    total: response.data?.total ?? 0,
    depthCapped: response.data?.depthCapped ?? false,
    depthCap: response.data?.depthCap ?? 0,
  };
}

export type DeviceInventoryLookupFailure = 'not_found' | 'ambiguous' | 'technical';

export class DeviceInventoryLookupError extends Error {
  constructor(public readonly reason: DeviceInventoryLookupFailure) {
    super(reason);
    this.name = 'DeviceInventoryLookupError';
  }
}

type DirectIFErrStatus = 'not_requested' | 'complete' | 'incomplete' | 'empty';

// Only an exact, current per-ifIndex measurement may turn zero into a fact.
function directIFErrMeasurement(
  interfaces: Array<{ if_index: number }>,
  rawErrors: unknown,
  rawStatus: unknown,
  source: unknown,
  isFromCache: boolean,
): { status: DirectIFErrStatus; byIndex: Map<number, { in_errors: number; out_errors: number }> } {
  const byIndex = new Map<number, { in_errors: number; out_errors: number }>();
  if (rawStatus === 'not_requested') return { status: 'not_requested', byIndex };
  if (rawStatus === 'empty' && interfaces.length === 0) return { status: 'empty', byIndex };
  if (rawStatus !== 'complete' || source !== 'snmp' || isFromCache || !Array.isArray(rawErrors)
    || interfaces.length === 0 || rawErrors.length !== interfaces.length) {
    return { status: 'incomplete', byIndex };
  }
  const expected = new Set<number>();
  for (const iface of interfaces) {
    if (!Number.isSafeInteger(iface.if_index) || expected.has(iface.if_index)) return { status: 'incomplete', byIndex: new Map() };
    expected.add(iface.if_index);
  }
  for (const item of rawErrors) {
    if (item === null || typeof item !== 'object') return { status: 'incomplete', byIndex: new Map() };
    const row = item as Record<string, unknown>;
    if (!Number.isSafeInteger(row.if_index) || !expected.has(row.if_index as number) || byIndex.has(row.if_index as number)) {
      return { status: 'incomplete', byIndex: new Map() };
    }
    const keys = ['in_errors', 'out_errors'] as const;
    if (keys.some((key) => !((typeof row[key] === 'number' && Number.isSafeInteger(row[key]))
      || (typeof row[key] === 'string' && /^\d+$/.test(row[key]) && Number.isSafeInteger(Number(row[key]))))
      || Number(row[key]) < 0)) {
      return { status: 'incomplete', byIndex: new Map() };
    }
    byIndex.set(row.if_index as number, {
      in_errors: Number(row.in_errors), out_errors: Number(row.out_errors),
    });
  }
  return { status: 'complete', byIndex };
}

export const devicesApi = {
  getPONAvailability: async (signal?: AbortSignal): Promise<PONAvailability> => {
    const { data } = await api.get<unknown>('/devices/pon-availability', { signal });
    return decodePONAvailability(data);
  },
  // Быстрый переход меню: exact inventory_number -> scoped device id -> карточка.
  resolveByInventoryNumber: async (inventoryNumber: string): Promise<number> => {
    try {
      const { data } = await api.get<{ id: number }>('/devices/by-inventory-number', {
        params: { inventory_number: inventoryNumber },
      });
      return data.id;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        throw new DeviceInventoryLookupError('not_found');
      }
      if (isAxiosError(error) && error.response?.status === 409) {
        throw new DeviceInventoryLookupError('ambiguous');
      }
      throw new DeviceInventoryLookupError('technical');
    }
  },

  // Список устройств
  list: async (filter: DeviceFilter = {}): Promise<DeviceListResponse> => {
    const params = new URLSearchParams();
    const setParam = (key: string, value: string | number | boolean | undefined | null) => {
      if (value === undefined || value === null) {
        return;
      }
      params.set(key, String(value));
    };

    if (filter.query) {
      const trimmed = filter.query.trim();
      if (trimmed) {
        setParam('query', trimmed);
      }
    }

    setParam('customer_id', filter.customer_id);
    setParam('node_id', filter.node_id);

    if (typeof filter.is_online === 'boolean') {
      setParam('is_online', filter.is_online);
    }

    if (filter.inventory_section_type_id !== undefined && filter.inventory_section_type_id !== null) {
      setParam('inventory_section_type_id', filter.inventory_section_type_id);
    }

    if (filter.inventory_section_id !== undefined && filter.inventory_section_id !== null) {
      setParam('inventory_section_id', filter.inventory_section_id);
    }

    if (filter.type_device) {
      setParam('type_device', filter.type_device);
    }

    // Метки: `params.append`, а НЕ setParam/`set` — у одного имени несколько значений
    // (`?tag_ids=1&tag_ids=2`), и `set` оставил бы только последнее, то есть фильтр по
    // двум меткам молча стал бы фильтром по одной. Семантика на сервере — И.
    if (filter.tag_ids && filter.tag_ids.length > 0) {
      for (const tagId of filter.tag_ids) {
        params.append('tag_ids', String(tagId));
      }
    }
    // Режим «без единой метки» взаимоисключающий с выбором меток (как в эталоне);
    // на сервере он имеет приоритет, здесь UI не даёт включить оба.
    if (filter.without_tags === true) {
      setParam('without_tags', true);
    }

    // Фильтры по числу портов уходят ПАРОЙ: операция без числа (и наоборот) на сервере
    // игнорируется целиком — половина фильтра молча сузила бы выдачу не туда.
    if (filter.port_free_op && filter.port_free_value !== undefined) {
      setParam('port_free_op', filter.port_free_op);
      setParam('port_free_value', filter.port_free_value);
    }
    if (filter.port_bad_op && filter.port_bad_value !== undefined) {
      setParam('port_bad_op', filter.port_bad_op);
      setParam('port_bad_value', filter.port_bad_value);
    }

    // Фильтр по МОДЕЛИ — список, как и метки: `append`, а не `set`. Эталон сравнивает
    // через `IN` (`DeviceHtml.php:11066-11072`), и `set` оставил бы только последнюю
    // модель, то есть отбор по двум молча стал бы отбором по одной.
    if (filter.model_ids && filter.model_ids.length > 0) {
      for (const modelId of filter.model_ids) {
        params.append('model_ids', String(modelId));
      }
    }

    setParam('page', filter.page);
    setParam('limit', filter.limit);

    if (filter.sort_by) {
      setParam('sort_by', filter.sort_by);
    }

    if (typeof filter.sort_desc === 'boolean') {
      setParam('sort_desc', filter.sort_desc);
    }

    const finalUrl = params.toString() ? `/devices?${params.toString()}` : '/devices';

    const { data } = await api.get<DeviceListResponse>(finalUrl);
    return normalizeDeviceListResponse(data, {
      page: filter.page,
      limit: filter.limit,
    });
  },

  // Получить устройство по ID
  getById: async (id: number): Promise<Device> => {
    const response = await api.get(`/devices/${id}`);
    return normalizeDeviceItem(unwrapDeviceData(response.data));
  },

  // Получить устройство с полными деталями (IP адреса, активность, связи)
  getDetails: async (id: number): Promise<Device> => {
    const response = await api.get(`/devices/${id}`);
    return normalizeDeviceItem(unwrapDeviceData(response.data));
  },

  // Создать устройство
  create: async (data: CreateDeviceRequest): Promise<Device> => {
    // Нормализуем hostname: пустая строка -> null
    const normalizedData = {
      ...data,
      hostname: data.hostname && data.hostname.trim() !== '' ? data.hostname : null,
      isSnmp: data.isSnmp ?? false,
    };
    const payload = serializeDevicePayload(normalizedData);
    const response = await api.post('/devices', payload);
    return normalizeDeviceItem(unwrapDeviceData(response.data));
  },

  // Обновить устройство
  update: async (id: number, data: UpdateDeviceRequest): Promise<Device> => {
    // Нормализуем hostname: пустая строка -> null
    const normalizedData = {
      ...data,
      hostname: data.hostname !== undefined && data.hostname !== null && data.hostname.trim() !== '' ? data.hostname : null,
      isSnmp: data.isSnmp ?? false,
    };

    const payload = serializeDevicePayload(normalizedData);
    if (normalizedData.fdb_method !== undefined) payload.fdbMethod = normalizedData.fdb_method;

    const response = await api.put(`/devices/${id}`, payload);
    const normalizedDevice = normalizeDeviceItem(unwrapDeviceData(response.data));
    return normalizedDevice;
  },

  // One ordinal, server-side merge and exact raw-column CAS. Never serialize
  // an entire client-edited SFP JSON map through generic device update.
  updateSFPPort: async (id: number, ordinal: number, expectedRaw: string | null, value: string): Promise<string> => {
    const { data } = await api.put<{ sfpAttList: string }>(`/devices/${id}/sfp-ports/${ordinal}`, {
      expectedRaw,
      value,
    });
    if (typeof data?.sfpAttList !== 'string') throw new Error('Invalid SFP port response');
    return data.sfpAttList;
  },

  // Сохранить заметку оператора по устройству.
  //
  // ⚠️ Отдельная ручка, а НЕ `update` выше. `update` шлёт тело устройства целиком и
  // принудительно подставляет `isSnmp: data.isSnmp ?? false` — сохранение одной заметки
  // через него ВЫКЛЮЧИЛО БЫ SNMP у устройства. Бэкенд на этом пути пишет ровно одну
  // колонку (`erp.devices.additional_comment`), как и эталон (action `save_memo`).
  updateNotes: async (id: number, notes: string): Promise<string | null> => {
    const { data } = await api.put<{ additionalComment?: string | null }>(`/devices/${id}/notes`, {
      notes,
    });
    return data?.additionalComment ?? null;
  },

  // Номера портов с включённой автокоммутацией.
  //
  // Саму автокоммутацию выполняет крон v4 по таблице `erp.devices_ifaces_auto_learning`
  // (`legacy/Cron/Cron2.php:32`); V5 только читает состояние.
  getAutoCommutationIfaces: async (id: number): Promise<number[]> => {
    const { data } = await api.get<{ ifaces?: number[] }>(`/devices/${id}/auto-commutation`);
    return data?.ifaces ?? [];
  },

  // Удалить устройство
  delete: async (id: number): Promise<void> => {
    await api.delete(`/devices/${id}`);
  },

  // Получить историю операций устройства.
  // Тип операции, автор и поиск по комментарию — СЕРВЕРНЫЕ: иначе фильтруется
  // загруженная страница, а число под таблицей относится ко всей истории.
  getOperationsHistory: async (
    id: number,
    filter: EmployeeHistoryFilter
  ): Promise<EmployeeHistoryResponse> => {
    const params: Record<string, string | number> = {
      date_from: filter.date_from,
      date_to: filter.date_to,
    };
    if (filter.type_id) params.type_id = filter.type_id;
    if (filter.employee_id) params.employee_id = filter.employee_id;
    if (filter.search) params.search = filter.search;
    if (filter.page) params.page = filter.page;
    if (filter.limit) params.limit = filter.limit;
    const response = await api.get(`/devices/${id}/operations-history`, { params });
    return response.data;
  },

  // Значения выпадашек истории операций — по всему периоду, а не по странице
  getOperationsHistoryFilters: async (
    id: number,
    period: { date_from: string; date_to: string }
  ): Promise<EmployeeHistoryFacets> => {
    const response = await api.get(`/devices/${id}/operations-history/filters`, {
      params: { date_from: period.date_from, date_to: period.date_to },
    });
    return response.data;
  },

  // Получить устройства клиента
  getByCustomerId: async (customerId: number): Promise<Device[]> => {
    const params = new URLSearchParams({
      customer_id: customerId.toString(),
      limit: '100',
    });
    const response = await api.get<DeviceListResponse>(`/devices?${params.toString()}`);
    const normalized = normalizeDeviceListResponse(response.data, { page: 1, limit: 100 });
    return normalized.items;
  },

  // Получить интерфейсы устройства
  getInterfaces: async (
    deviceId: number,
    options?: {
      view?: 'compact' | 'full' | 'errors-only';
      includeErrors?: boolean;
    },
  ): Promise<DeviceInterface[]> => {
    const params = new URLSearchParams();
    if (options?.view) {
      params.set('view', options.view);
    }
    if (options?.includeErrors !== undefined) {
      params.set('include_errors', String(options.includeErrors));
    }
    const queryString = params.toString();
    const url = `/devices/${deviceId}/interfaces${queryString ? `?${queryString}` : ''}`;
    // ⚠️ Провод отдаёт { interfaces, metadata }, а не голый массив — объявленный
    // тип врал про форму ответа, и потребитель падал на переборе объекта.
    // Приводим к обещанному массиву здесь, а не в каждом вызывающем месте.
    const response = await api.get<DeviceInterface[] | { interfaces?: DeviceInterface[] }>(url);
    const payload = response.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    return Array.isArray(payload?.interfaces) ? payload.interfaces : [];
  },

  // Получить интерфейсы устройства с метаданными (stale, lastPoll, physicalCount, totalCount)
  getInterfacesWithMeta: async (
    deviceId: number,
    options?: {
      view?: 'compact' | 'full' | 'errors-only';
      includeErrors?: boolean;
      cacheOnly?: boolean;
      showAll?: boolean;
    },
    signal?: AbortSignal,
  ): Promise<{
    interfaces: DeviceInterface[];
    meta: {
      isStale?: boolean;
      lastPoll?: string | null;
      physicalCount?: number;
      totalCount?: number;
      source?: string;
      truncated?: boolean;
    };
  }> => {
    const params = new URLSearchParams();
    if (options?.view) {
      params.set('view', options.view);
    }
    if (options?.includeErrors !== undefined) {
      params.set('include_errors', String(options.includeErrors));
    }
    if (options?.cacheOnly === true) {
      params.set('cache_only', 'true');
    }
    if (options?.showAll === true) {
      params.set('showAll', 'true');
    }
    const queryString = params.toString();
    const url = `/devices/${deviceId}/interfaces${queryString ? `?${queryString}` : ''}`;

    // ✅ ФАЗА 2: Backend возвращает объект с interfaces и metadata
    const response = await api.get<{
      interfaces: DeviceInterface[];
      metadata?: {
        physicalCount?: number;
        totalCount?: number;
        is_stale?: boolean;
        last_poll?: string;
        source?: string;
        truncated?: boolean;
      };
    }>(url, { signal, skipRetry: true });

    // ✅ ДИАГНОСТИКА: Проверяем интерфейсы с большими ifIndex и пустыми именами из baseQuery
    const interfaces = response.data.interfaces || [];
    const largeIfIndexEmptyNames = interfaces.filter((iface: any) => iface.if_index > 1000000 && (!iface.name || iface.name.trim() === ''));
    const largeIfIndexWithNames = interfaces.filter((iface: any) => iface.if_index > 1000000 && iface.name && iface.name.trim() !== '');
    if (largeIfIndexEmptyNames.length > 0) {
      console.warn('[devicesApi.ts] ⚠️ WARNING: Interfaces from baseQuery (Poller cache) with large ifIndex have empty names', {
        count: largeIfIndexEmptyNames.length,
        totalInterfaces: interfaces.length,
        source: response.data.metadata?.source,
        sampleInterfaces: largeIfIndexEmptyNames.slice(0, 3).map((iface: any) => ({ if_index: iface.if_index, name: iface.name, description: iface.description, type: iface.type })),
      });
    }
    // Читаем заголовки (axios может вернуть их в разных регистрах)
    const headers = response.headers || {};
    const isStale =
      headers['x-data-stale'] === 'true' ||
      headers['X-Data-Stale'] === 'true' ||
      String(headers['x-data-stale'] || '').toLowerCase() === 'true' ||
      response.data.metadata?.is_stale === true;

    // X-Last-Poll приходит как Unix timestamp (число в секундах)
    let lastPoll: string | null = null;
    const lastPollHeader = headers['x-last-poll'] || headers['X-Last-Poll'];
    if (lastPollHeader) {
      const timestamp = typeof lastPollHeader === 'string'
        ? parseInt(lastPollHeader, 10)
        : Number(lastPollHeader);

      // Проверяем что это валидный timestamp (не слишком большое число)
      // Максимальный разумный timestamp: 2100-01-01 = 4102444800
      if (!isNaN(timestamp) && timestamp > 0 && timestamp < 4102444800) {
        lastPoll = new Date(timestamp * 1000).toISOString();
      } else {
        console.warn('Invalid X-Last-Poll timestamp:', lastPollHeader);
      }
    } else if (response.data.metadata?.last_poll) {
      // Используем last_poll из metadata если заголовок отсутствует
      lastPoll = response.data.metadata.last_poll;
    }

    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ: Нормализуем статусы из чисел в строки
    // Backend возвращает admin_status и oper_status как числа (1=up, 2=down, 3=testing)
    // Но DeviceInterface ожидает строки ("up", "down", "testing")
    const normalizeStatus = (status: number | string | undefined): string => {
      if (status === undefined || status === null) {
        return 'unknown';
      }
      if (typeof status === 'string') {
        const lower = status.toLowerCase();
        if (['up', 'down', 'testing', 'unknown'].includes(lower)) {
          return lower;
        }
        return 'unknown';
      }
      if (typeof status === 'number') {
        switch (status) {
          case 1:
            return 'up';
          case 2:
            return 'down';
          case 3:
            return 'testing';
          default:
            return 'unknown';
        }
      }
      return 'unknown';
    };

    // Нормализуем интерфейсы
    const normalizedInterfaces = interfaces.map((iface: any, index: number) => {
      const normalized = {
        ...iface,
        admin_status: normalizeStatus(iface.admin_status),
        oper_status: normalizeStatus(iface.oper_status),
        // cache_only can return a V5 L3 snapshot whose producer copied default
        // zero counters without any IFErr provenance. It is not a proof of zero.
        ...(options?.cacheOnly ? { errors_measured: false, discards_measured: false } : {}),
      };

      // ✅ ДИАГНОСТИКА: Проверяем, не потерялось ли имя при нормализации для больших ifIndex
      if (iface.if_index > 1000000) {
        if (iface.name && !normalized.name) {
          console.error('[devicesApi.ts] ❌ ERROR: Name lost during normalization in getInterfacesWithMeta!', {
            if_index: iface.if_index,
            nameBefore: iface.name,
            nameAfter: normalized.name,
            description: iface.description,
            normalizedKeys: Object.keys(normalized),
            originalKeys: Object.keys(iface),
          });
        } else if (iface.name && normalized.name && iface.name !== normalized.name) {
          console.warn('[devicesApi.ts] ⚠️ WARNING: Name changed during normalization in getInterfacesWithMeta!', {
            if_index: iface.if_index,
            nameBefore: iface.name,
            nameAfter: normalized.name,
          });
        } else if (iface.name && normalized.name && iface.name === normalized.name && (index < 3 || iface.if_index === 268503552)) {
          // Логируем только первые 3 или конкретный проблемный интерфейс для подтверждения
        }
      }

      return normalized;
    });

    // ✅ ДИАГНОСТИКА: Проверяем имена после нормализации в getInterfacesWithMeta
    const largeIfIndexAfterNormalize = normalizedInterfaces.filter((iface: any) => iface.if_index > 1000000);
    const largeIfIndexWithNamesAfterNormalize = largeIfIndexAfterNormalize.filter((iface: any) => iface.name && iface.name.trim() !== '');
    const largeIfIndexEmptyNamesAfterNormalize = largeIfIndexAfterNormalize.filter((iface: any) => !iface.name || iface.name.trim() === '');

    if (largeIfIndexAfterNormalize.length > 0) {
      if (largeIfIndexEmptyNamesAfterNormalize.length > 0) {
        console.warn('[devicesApi.ts] ⚠️ WARNING: Some large ifIndex interfaces have empty names AFTER normalization in getInterfacesWithMeta', {
          total: largeIfIndexAfterNormalize.length,
          withNames: largeIfIndexWithNamesAfterNormalize.length,
          emptyNames: largeIfIndexEmptyNamesAfterNormalize.length,
          source: response.data.metadata?.source,
          sampleEmpty: largeIfIndexEmptyNamesAfterNormalize.slice(0, 3).map((iface: any) => ({ if_index: iface.if_index, name: iface.name, description: iface.description })),
        });
      }
    }

    return {
      interfaces: normalizedInterfaces,
      meta: {
        isStale: Boolean(isStale),
        lastPoll,
        physicalCount: response.data.metadata?.physicalCount,
        totalCount: response.data.metadata?.totalCount,
        source: response.data.metadata?.source,
        truncated: response.data.metadata?.truncated,
      },
    };
  },

  // Получить интерфейсы через Direct SNMP (прямой запрос к устройству)
  getInterfacesTraffic: async (
    deviceId: number,
    opts?: { ifIndexes?: number[] },
    signal?: AbortSignal,
  ): Promise<{
    traffic: Array<{
      if_index: number;
      in_octets: number;
      out_octets: number;
      rx: number;
      tx: number;
    }>;
    source: string;
    updated_at: string;
  }> => {
    const params = new URLSearchParams();
    if (opts?.ifIndexes && opts.ifIndexes.length > 0) {
      params.set('if_indexes', opts.ifIndexes.join(','));
    }
    const queryString = params.toString();
    const url = `/devices/${deviceId}/interfaces/traffic${queryString ? `?${queryString}` : ''}`;

    // SNMP traffic on large devices can take longer than ordinary API reads.
    // Do not log response bodies here: typed refusal details stay in the UI parser.
    const response = await api.get<{
      traffic: Array<{
        if_index: number;
        in_octets: number;
        out_octets: number;
        rx: number;
        tx: number;
      }>;
      source: string;
      updated_at: string;
    }>(url, {
      timeout: 120000,
      signal,
      skipRetry: true,
    });

    return response.data;
  },

  // Получить историю трафика интерфейсов из Redis
  getInterfacesTrafficHistory: async (
    deviceId: number,
    opts?: { ifIndexes?: number[] },
  ): Promise<{
    device_id: number;
    history: Array<{
      if_index: number;
      history: Array<{
        timestamp: string;
        in_octets: number;
        out_octets: number;
        in_errors: number;
        out_errors: number;
        source: string;
      }>;
    }>;
  }> => {
    const params = new URLSearchParams();
    if (opts?.ifIndexes && opts.ifIndexes.length > 0) {
      params.set('if_indexes', opts.ifIndexes.join(','));
    }
    const queryString = params.toString();
    const url = `/devices/${deviceId}/interfaces/traffic/history${queryString ? `?${queryString}` : ''}`;

    try {
      const response = await api.get<{
        device_id: number;
        history: Array<{
          if_index: number;
          history: Array<{
            timestamp: string;
            in_octets: number;
            out_octets: number;
            in_errors: number;
            out_errors: number;
            source: string;
          }>;
        }>;
      }>(url, {
        timeout: 10000, // 10 секунд
      });

      return response.data;
    } catch (error) {
      console.error('[devicesApi.ts] ❌ ERROR: getInterfacesTrafficHistory failed', {
        deviceId,
        url,
        error: error instanceof Error ? {
          message: error.message,
          name: error.name,
        } : error,
      });
      throw error;
    }
  },

  getInterfacesDirect: async (
    deviceId: number,
    options?: {
      ifIndexes?: number[]; // Список индексов интерфейсов (пусто = все)
      includeErrors?: boolean;
      basicOnly?: boolean; // ✅ UX УЛУЧШЕНИЕ (Phase 0): true = только базовые OID (быстрее)
      oidLevel?: number; // ✅ ФАЗА 2: 1=essential (3 OID), 2=important (7 OID), 3=extended (12 OID)
      physicalOnly?: boolean; // ✅ ИСПРАВЛЕНИЕ (2026-01-06): true = только физические интерфейсы (по умолчанию), false = все интерфейсы
    },
    signal?: AbortSignal, // ✅ Support for AbortSignal (React Query passes this)
  ): Promise<{
    interfaces: DeviceInterface[];
    interface_errors_status: DirectIFErrStatus;
    interface_discards_status: 'unavailable';
    interface_errors?: Array<{
      if_index: number;
      in_errors: number;
      out_errors: number;
      in_discards: number;
      out_discards: number;
    }>;
    source: 'snmp' | 'db';
    updated_at: string;
    effective_oid_level?: number;
    poll_type?: string;
    is_guaranteed_fresh?: boolean;
  }> => {
    const params = new URLSearchParams();
    if (options?.ifIndexes && options.ifIndexes.length > 0) {
      params.set('if_indexes', options.ifIndexes.join(','));
    }
    if (options?.includeErrors !== undefined) {
      params.set('include_errors', String(options.includeErrors));
    }
    // ✅ UX УЛУЧШЕНИЕ: Параметр basic_only для ленивой загрузки OID
    if (options?.basicOnly !== undefined) {
      params.set('basic_only', String(options.basicOnly));
    }
    // ✅ ФАЗА 2: Параметр oid_level для progressive loading (1=essential, 2=important, 3=extended)
    if (options?.oidLevel !== undefined && options.oidLevel >= 1 && options.oidLevel <= 3) {
      params.set('oid_level', String(options.oidLevel));
    }
    // ✅ ИСПРАВЛЕНИЕ (2026-01-06): Параметр physical_only для фильтрации физических интерфейсов
    // По умолчанию true (только физические), передаем только если false
    if (options?.physicalOnly !== undefined) {
      params.set('physical_only', String(options.physicalOnly));
    } else {
      // По умолчанию true - только физические интерфейсы
      params.set('physical_only', 'true');
    }
    const queryString = params.toString();
    const url = `/devices/${deviceId}/interfaces/direct${queryString ? `?${queryString}` : ''}`;

    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-07): Синхронизированы таймауты с useDeviceInterfacesDirect
    // L1 (essential): 45s, L2 (important): 60s, L3 (extended): 120s
    // Это соответствует таймаутам на frontend и предотвращает зависание запросов
    const getTimeoutMs = (level?: number): number => {
      if (level === 1) return 45000;  // ✅ УВЕЛИЧЕНО: 45s для L1 (было 25s)
      if (level === 2) return 60000;  // ✅ УВЕЛИЧЕНО: 60s для L2 (было 40s)
      if (level === 3) return 150000; // ✅ УВЕЛИЧЕНО: 150s для L3 (было 100s)
      return 45000; // дефолт L1
    };

    const timeout = getTimeoutMs(options?.oidLevel);

    const response = await api.get(url, {
      signal, // ✅ Pass AbortSignal to axios
      timeout, // ✅ Динамический таймаут в зависимости от уровня OID
      skipRetry: true,
    });

    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-08): Извлекаем метаданные кеша из заголовков
    // Backend отправляет X-Data-Source (cache/cache_stale/direct) и X-Cache-Fresh (true/false)
    // для оптимизации отображения данных
    const dataSource = response.headers['x-data-source'] as string | undefined;
    const cacheFresh = response.headers['x-cache-fresh'] === 'true';
    const isFromCache = dataSource === 'cache' || dataSource === 'cache_stale';
    const isCacheStale = dataSource === 'cache_stale';
    const isCacheFresh = dataSource === 'cache' && !isCacheStale; // ✅ ИСПРАВЛЕНИЕ: Определяем isCacheFresh


    // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ: Нормализуем статусы из чисел в строки
    // Backend возвращает admin_status и oper_status как числа (1=up, 2=down, 3=testing)
    // Но DeviceInterface ожидает строки ("up", "down", "testing")
    const normalizeStatus = (status: number | string | undefined): string => {
      if (status === undefined || status === null) {
        return 'unknown';
      }
      if (typeof status === 'string') {
        const lower = status.toLowerCase();
        if (['up', 'down', 'testing', 'unknown'].includes(lower)) {
          return lower;
        }
        return 'unknown';
      }
      if (typeof status === 'number') {
        switch (status) {
          case 1:
            return 'up';
          case 2:
            return 'down';
          case 3:
            return 'testing';
          default:
            return 'unknown';
        }
      }
      return 'unknown';
    };

    // Нормализуем интерфейсы
    const interfaces = response.data.interfaces || [];
    const ifErr = directIFErrMeasurement(
      interfaces, response.data.interface_errors, response.data.interface_errors_status,
      response.data.source, isFromCache,
    );
    // ✅ ДИАГНОСТИКА: Проверяем интерфейсы с большими ifIndex и пустыми именами
    const largeIfIndexEmptyNames = interfaces.filter((iface: any) => iface.if_index > 1000000 && (!iface.name || iface.name.trim() === ''));
    const largeIfIndexWithNames = interfaces.filter((iface: any) => iface.if_index > 1000000 && iface.name && iface.name.trim() !== '');
    if (largeIfIndexEmptyNames.length > 0) {
      console.warn('[devicesApi.ts] ⚠️ WARNING: Interfaces with large ifIndex have empty names', {
        count: largeIfIndexEmptyNames.length,
        source: response.data.source,
        dataSource,
        isFromCache,
        isCacheFresh,
        isCacheStale,
        sampleInterfaces: largeIfIndexEmptyNames.slice(0, 3).map((iface: any) => ({ if_index: iface.if_index, name: iface.name, description: iface.description, type: iface.type })),
      });
    }
    const normalizedInterfaces = interfaces.map((iface: any, index: number) => {
      const counters = ifErr.status === 'complete' ? ifErr.byIndex.get(iface.if_index) : undefined;
      const normalized = {
        ...iface,
        admin_status: normalizeStatus(iface.admin_status),
        oper_status: normalizeStatus(iface.oper_status),
        in_errors: counters?.in_errors ?? 0,
        out_errors: counters?.out_errors ?? 0,
        in_discards: 0,
        out_discards: 0,
        errors_measured: ifErr.status === 'complete',
        discards_measured: false,
      };

      // ✅ ДИАГНОСТИКА: Проверяем, не потерялось ли имя при нормализации
      if (iface.if_index > 1000000) {
        if (iface.name && !normalized.name) {
          console.error('[devicesApi.ts] ❌ ERROR: Name lost during normalization!', {
            if_index: iface.if_index,
            nameBefore: iface.name,
            nameAfter: normalized.name,
            description: iface.description,
            normalizedKeys: Object.keys(normalized),
            originalKeys: Object.keys(iface),
          });
        } else if (iface.name && normalized.name && iface.name !== normalized.name) {
          console.warn('[devicesApi.ts] ⚠️ WARNING: Name changed during normalization!', {
            if_index: iface.if_index,
            nameBefore: iface.name,
            nameAfter: normalized.name,
          });
        } else if (iface.name && normalized.name && iface.name === normalized.name && (index < 3 || iface.if_index === 268503552)) {
          // Логируем только первые 3 или конкретный проблемный интерфейс для подтверждения
        }
      }

      return normalized;
    });

    // ✅ ДИАГНОСТИКА: Проверяем имена после нормализации
    const largeIfIndexAfterNormalize = normalizedInterfaces.filter((iface: any) => iface.if_index > 1000000);
    const largeIfIndexWithNamesAfterNormalize = largeIfIndexAfterNormalize.filter((iface: any) => iface.name && iface.name.trim() !== '');
    const largeIfIndexEmptyNamesAfterNormalize = largeIfIndexAfterNormalize.filter((iface: any) => !iface.name || iface.name.trim() === '');

    if (largeIfIndexAfterNormalize.length > 0) {
      if (largeIfIndexEmptyNamesAfterNormalize.length > 0) {
        console.warn('[devicesApi.ts] ⚠️ WARNING: Some large ifIndex interfaces have empty names AFTER normalization', {
          total: largeIfIndexAfterNormalize.length,
          withNames: largeIfIndexWithNamesAfterNormalize.length,
          emptyNames: largeIfIndexEmptyNamesAfterNormalize.length,
          sampleEmpty: largeIfIndexEmptyNamesAfterNormalize.slice(0, 3).map((iface: any) => ({ if_index: iface.if_index, name: iface.name, description: iface.description })),
        });
      }
    }

    return {
      ...response.data,
      interfaces: normalizedInterfaces,
      interface_errors_status: ifErr.status,
      interface_discards_status: 'unavailable',
      interface_errors: ifErr.status === 'complete' ? response.data.interface_errors : [],
      // ✅ КРИТИЧНОЕ ИСПРАВЛЕНИЕ (2026-01-08): Добавляем метаданные кеша для оптимизации
      _cacheMetadata: {
        dataSource: dataSource || 'direct',
        isFromCache,
        isCacheFresh: cacheFresh,
        isCacheStale,
      },
    };
  },

  // Получить FDB таблицу устройства
  getFDB: async (deviceId: number, signal?: AbortSignal): Promise<FDBEntry[]> => {
    return withNotFoundFallback(
      async () => {
        const response = signal
          ? await api.get<FDBEntry[]>(`/devices/${deviceId}/fdb`, { signal, skipRetry: true })
          : await api.get<FDBEntry[]>(`/devices/${deviceId}/fdb`);
        return response.data;
      },
      () => [],
    );
  },

  getARPCapabilityV1: async (deviceId: number, signal?: AbortSignal): Promise<ARPCapabilityV1Response> => {
    const response = await api.get<ARPCapabilityV1Response>(
      `/devices/${deviceId}/arp/capability-v1`, { signal },
    );
    return response.data;
  },

  getARPReadFacadeV1: async (
    deviceId: number,
    page = 1,
    limit = 50,
    opts?: ARPReadFacadeV1Query,
  ): Promise<ARPReadFacadeV1Response> => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (opts?.filters?.ip !== undefined) params.set('filter[ip]', opts.filters.ip);
    if (opts?.filters?.mac !== undefined) params.set('filter[mac]', opts.filters.mac);
    if (opts?.filters?.vlan_id !== undefined) params.set('filter[vlan_id]', String(opts.filters.vlan_id));
    if (opts?.filters?.if_index !== undefined) params.set('filter[if_index]', String(opts.filters.if_index));
    if (opts?.filters?.type !== undefined) params.set('filter[type]', opts.filters.type);
    if (opts?.sort_by !== undefined) params.set('sort_by', opts.sort_by);
    if (opts?.sort_desc !== undefined) params.set('sort_desc', String(opts.sort_desc));

    const response = await api.get<ARPReadFacadeV1Response>(
      `/devices/${deviceId}/arp/read-v1?${params.toString()}`,
      { signal: opts?.signal, skipRetry: true },
    );
    return response.data;
  },

  // Пагинированный FDB (items + pagination)
  getFdbPaged: async (
    deviceId: number,
    page = 1,
    limit = 50,
    opts?: { filters?: { mac?: string; port?: number; vlan_id?: number; type?: string }; sort_by?: string; sort_desc?: boolean },
  ): Promise<{ items: FDBEntry[]; pagination: { page: number; limit: number; total: number; total_pages: number } }> => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (opts?.filters?.mac) params.set('filter[mac]', String(opts.filters.mac));
    if (opts?.filters?.port != null) params.set('filter[port]', String(opts.filters.port));
    if (opts?.filters?.vlan_id != null) params.set('filter[vlan_id]', String(opts.filters.vlan_id));
    if (opts?.filters?.type) params.set('filter[type]', String(opts.filters.type));
    if (opts?.sort_by) params.set('sort_by', String(opts.sort_by));
    if (opts?.sort_desc != null) params.set('sort_desc', String(opts.sort_desc));
    const url = `/devices/${deviceId}/fdb/paged?${params.toString()}`;

    // Пытаемся использовать серверную пагинацию; если 404 — фоллбек на полный список с client-side slice
    return withNotFoundFallback(
      async () => {
        const { data } = await api.get(url);
        const pagination = (data?.pagination ?? {}) as Record<string, unknown>;
        const items: FDBEntry[] = Array.isArray(data?.items) ? data.items : [];
        const total = Number(pagination.total ?? items.length);
        const limitNum = Number(pagination.limit ?? limit);
        return {
          items,
          pagination: {
            page: Number(pagination.page ?? page),
            limit: limitNum,
            total,
            total_pages: Number(
              pagination.total_pages ??
              (total && limitNum ? Math.ceil(total / limitNum) : 0),
            ),
          },
        };
      },
      async () => {
        // Фоллбек: получаем весь список и режем по странице
        const all = await devicesApi.getFDB(deviceId);
        const start = (page - 1) * limit;
        const end = start + limit;
        const items = all.slice(start, end);
        const total = all.length;
        return {
          items,
          pagination: {
            page,
            limit,
            total,
            total_pages: total && limit ? Math.ceil(total / limit) : 0,
          },
        };
      },
    );
  },

  // Пагинированный VLAN (items + pagination)
  getVlanPaged: async (
    deviceId: number,
    page = 1,
    limit = 50,
    opts?: { filters?: { vlan_id?: number; name?: string; status?: string }; sort_by?: string; sort_desc?: boolean; signal?: AbortSignal },
  ): Promise<{ items: DeviceVlanPagedItem[]; pagination: { page: number; limit: number; total: number; total_pages: number } }> => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (opts?.filters?.vlan_id != null) params.set('filter[vlan_id]', String(opts.filters.vlan_id));
    if (opts?.filters?.name) params.set('filter[name]', String(opts.filters.name));
    if (opts?.filters?.status) params.set('filter[status]', String(opts.filters.status));
    if (opts?.sort_by) params.set('sort_by', String(opts.sort_by));
    if (opts?.sort_desc != null) params.set('sort_desc', String(opts.sort_desc));
    const url = `/devices/${deviceId}/vlan/paged?${params.toString()}`;

    // This read can trigger a direct device poll. The shared Axios 5xx retry
    // would issue another poll before React Query sees an admission refusal.
    // A missing endpoint is an error, not evidence of an empty VLAN table.
    const { data } = await api.get(url, { skipRetry: true, signal: opts?.signal });
    const pagination = (data?.pagination ?? {}) as Record<string, unknown>;
    const items: DeviceVlanPagedItem[] = Array.isArray(data?.items) ? data.items : [];
    const total = Number(pagination.total ?? items.length);
    const limitNum = Number(pagination.limit ?? limit);
    return {
      items,
      pagination: {
        page: Number(pagination.page ?? page),
        limit: limitNum,
        total,
        total_pages: Number(
          pagination.total_pages ?? (total && limitNum ? Math.ceil(total / limitNum) : 0),
        ),
      },
    };
  },

  // Поиск по MAC адресу
  findMAC: async (macAddress: string): Promise<MacSearchResult[]> => {
    const response = await api.get<MacSearchResult[]>(`/devices/search/mac/${macAddress}`);
    return response.data;
  },

  // ✅ РЕФАКТОРИНГ (2026-01-XX): Удален getPollerInterfaces - используйте /devices/:id/interfaces/direct
  getPollerPon: async (deviceId: number, task: string): Promise<PollerPonCacheResponse> => {
    return withNotFoundFallback(
      async () => {
        const response = await api.get<PollerPonCacheResponse>(`/devices/${deviceId}/poller/pon/${task}`);
        return response.data;
      },
      () => createEmptyPollerCacheResponse(task || 'PonTask') as PollerPonCacheResponse,
    );
  },
  // ✅ РЕФАКТОРИНГ (2026-01-XX): Удалены getPollerVlan, getPollerFdb, getPollerArp
  // Используйте /devices/:id/vlan/paged, /devices/:id/fdb, /devices/:id/arp (Direct SNMP)

  // ✅ РЕФАКТОРИНГ (2026-01-XX): Удалены syncPollerInterfaces, startPollerIfaces
  // Интерфейсы теперь используют Direct SNMP, poller используется только для ONU опросов

  // Получить настройки планировщика Poller
  getObserver: async (deviceId: number): Promise<DeviceObserverResponse> => {
    const response = await api.get<DeviceObserverResponse>(`/devices/${deviceId}/observer`);
    const observer = response.data?.observer ?? {
      deviceId,
      profileId: null,
      isPolling: false,
      isAlert: false,
    };

    return {
      observer: {
        deviceId: observer.deviceId ?? deviceId,
        profileId: observer.profileId ?? null,
        isPolling: observer.isPolling ?? false,
        isAlert: observer.isAlert ?? false,
      },
      profiles: response.data?.profiles ?? [],
    };
  },

  // Обновить настройки планировщика Poller
  updateObserver: async (
    deviceId: number,
    payload: UpdateDeviceObserverPayload,
  ): Promise<DeviceObserverConfig> => {
    const requestBody = {
      profileId: payload.profileId ?? null,
      isPolling: payload.isPolling,
      isAlert: payload.isAlert ?? false,
    };

    const response = await api.put<{
      deviceId: number;
      profileId: number | null;
      isPolling: boolean;
      isAlert: boolean;
    }>(`/devices/${deviceId}/observer`, requestBody);

    const data = response.data;
    return {
      deviceId: data.deviceId ?? deviceId,
      profileId: data.profileId ?? null,
      isPolling: data.isPolling,
      isAlert: data.isAlert,
    };
  },

  // Управление admin-статусом интерфейсов
  controlInterface: async (
    deviceId: number,
    payload: { action: InterfaceAdminAction; interfaces: number[] },
  ): Promise<ControlInterfaceResponse> => {
    const actionEnum = payload.action === 'up'
      ? 'INTERFACE_ADMIN_ACTION_UP'
      : 'INTERFACE_ADMIN_ACTION_DOWN';

    const response = await api.post<ControlInterfaceResponse>(
      `/devices/${deviceId}/interfaces/control`,
      {
        deviceId,
        interfaceIndexes: payload.interfaces,
        action: actionEnum,
      },
    );

    return response.data;
  },

  // Запустить опрос устройства
  pollDevice: async (deviceId: number, request: PollDeviceRequest): Promise<PollDeviceResponse> => {
    const response = await api.post<PollDeviceRawResponse>(`/devices/${deviceId}/poller`, request);
    const { data, meta } = response.data;
    const message = meta?.message?.ru ?? meta?.message?.en ?? '';

    return {
      taskId: data.taskId,
      status: data.status,
      message,
      createdAt: meta?.createdAt ?? '',
    };
  },

  // Зарегистрировать ожидание результата Poller-задачи
  registerPollerAwaiting: async (taskId: string): Promise<void> => {
    const trimmed = taskId?.trim();
    if (!trimmed) {
      return;
    }
    await api.post(`/poller/tasks/${encodeURIComponent(trimmed)}/await`);
  },

  // Cable Test
  // Запустить тест кабеля
  startCableTest: async (deviceId: number, request: StartCableTestRequest = {}): Promise<StartCableTestResponse> => {
    return withNotFoundFallback(
      async () => {
        const { data } = await api.post<StartCableTestResponse>(`/devices/${deviceId}/cable-test`, request);
        return {
          deviceId: data.deviceId ?? deviceId,
          dateAdd: data.dateAdd ?? 0,
          status: data.status ?? '',
          message: data.message ?? '',
          createdAt: data.createdAt ?? '',
        };
      },
      () => ({
        deviceId,
        dateAdd: 0,
        status: 'not_supported',
        message: 'devices:errors.cable_test_unavailable',
        createdAt: new Date().toISOString(),
      }),
    );
  },

  getCableTestHistory: async (
    deviceId: number,
    page = 1,
    limit = 20,
    filters: { isFinish?: boolean; dateFrom?: number; dateTo?: number } = {},
  ): Promise<CableTestHistoryResponse> => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));

    if (filters.isFinish !== undefined) {
      params.set('isFinish', String(filters.isFinish));
    }
    if (filters.dateFrom !== undefined) {
      params.set('dateFrom', String(filters.dateFrom));
    }
    if (filters.dateTo !== undefined) {
      params.set('dateTo', String(filters.dateTo));
    }

    const query = params.toString();
    const url = query
      ? `/devices/${deviceId}/cable-test/history?${query}`
      : `/devices/${deviceId}/cable-test/history`;

    return withNotFoundFallback(
      async () => {
        const { data } = await api.get<CableTestHistoryResponse>(url);
        const rawItems = Array.isArray(data.items) ? data.items : [];
        const items = rawItems.map((item) => ({
          ...item,
          results: item.results?.map(normalizeCablePortResult),
        }));

        const rawPagination = (data.pagination ?? {}) as Record<string, unknown>;
        const pageValue = normalizeNumber(rawPagination.page) ?? page;
        const limitValue = normalizeNumber(rawPagination.limit) ?? limit;
        const totalValue = normalizeNumber(rawPagination.total) ?? items.length;
        const totalPagesValue =
          normalizeNumber(rawPagination.totalPages) ??
          normalizeNumber(rawPagination.total_pages) ??
          (limitValue > 0 ? Math.ceil(totalValue / limitValue) : 0);

        return {
          items,
          pagination: {
            page: pageValue ?? page,
            limit: limitValue ?? limit,
            total: totalValue ?? items.length,
            totalPages: totalPagesValue ?? (limit > 0 ? Math.ceil(items.length / limit) : 0),
          },
        };
      },
      () => ({
        items: [],
        pagination: { page, limit, total: 0, totalPages: 0 },
      }),
    );
  },

  getCableTestResults: async (deviceId: number, dateAdd: number): Promise<CableTestResultResponse> => {
    const { data } = await api.get<CableTestResultResponse>(
      `/devices/${deviceId}/cable-test/results/${dateAdd}`,
    );

    return {
      deviceId: data.deviceId ?? deviceId,
      deviceName: data.deviceName,
      dateAdd: data.dateAdd ?? dateAdd,
      isFinish: data.isFinish ?? false,
      createdAt: data.createdAt ?? '',
      results: (data.results ?? []).map(normalizeCablePortResult),
    };
  },

  getActivityLog: async (
    deviceId: number,
    filters: {
      page?: number;
      limit?: number;
      is_down?: boolean;
      date_from?: number;
      date_to?: number;
    } = {},
  ): Promise<DeviceActivityLogResponse> => {
    const params = new URLSearchParams();
    const appendParam = (key: string, value: number | boolean | undefined) => {
      if (value === undefined || value === null) {
        return;
      }
      params.append(key, String(value));
    };

    appendParam('page', filters.page);
    appendParam('limit', filters.limit);
    appendParam('is_down', filters.is_down);
    appendParam('date_from', filters.date_from);
    appendParam('date_to', filters.date_to);

    const query = params.toString();
    const url = query
      ? `/devices/${deviceId}/activity-log?${query}`
      : `/devices/${deviceId}/activity-log`;

    const { data } = await api.get<DeviceActivityLogResponse>(url);
    return data;
  },

  getONUList: async (
    deviceId: number,
    filter: ONUFilter & { page: number; limit: number; sort_by?: string; sort_desc?: boolean },
  ): Promise<ONUListResponse> => {
    const params = new URLSearchParams();

    // Обязательные параметры пагинации
    params.append('page', String(filter.page || 1));
    params.append('limit', String(filter.limit || 20));

    // Опциональные параметры
    if (filter.query && filter.query.trim()) {
      params.append('query', filter.query.trim());
    }

    if (filter.olt_if_index !== undefined && filter.olt_if_index !== null) {
      params.append('olt_if_index', String(filter.olt_if_index));
    }

    // Boolean параметры - передаем только если они явно установлены в true
    // Backend имеет default значения для всех boolean параметров
    // Gin парсит "true"/"1" как true, "false"/"0" как false, но лучше не передавать false
    if (filter.is_online === true) {
      params.append('is_online', 'true');
    }
    // Не передаем is_online=false, так как это значение по умолчанию

    if (filter.is_unknown === true) {
      params.append('is_unknown', 'true');
    }

    if (filter.is_level_bad === true) {
      params.append('is_level_bad', 'true');
    }

    // Сортировка
    // Backend имеет default="olt_if_index" для sort_by
    const sortBy = filter.sort_by?.trim();
    if (sortBy) {
      // Валидируем, что это безопасное значение (только буквы, цифры, подчеркивания, запятые, пробелы)
      if (/^[a-zA-Z0-9_,\s]+$/.test(sortBy)) {
        params.append('sort_by', sortBy);
      } else if (import.meta.env.DEV) {
        console.warn('[getONUList] Недопустимое значение sort_by, используем значение по умолчанию:', sortBy);
      }
    }

    // sort_desc передаем только если он true (по умолчанию false на backend)
    if (filter.sort_desc === true) {
      params.append('sort_desc', 'true');
    }

    const query = params.toString();
    const url = `/devices/${deviceId}/onu${query ? `?${query}` : ''}`;


    try {
      const { data } = await api.get<ONUListResponse>(url);
      const pagination = data.pagination ?? {
        page: filter.page,
        limit: filter.limit,
        total: data.items.length,
        total_pages: filter.limit > 0 ? Math.ceil(data.items.length / filter.limit) : 0,
      };

      const statisticsSource = data.statistics ?? {
        total: data.items.length,
        online: 0,
        offline: 0,
        unknown: 0,
        bad_level: 0,
      };

      const statsRecord = statisticsSource as unknown as Record<string, unknown>;
      const badLevel =
        normalizeNumber(statsRecord.bad_level) ??
        normalizeNumber(statsRecord.badLevel) ??
        statisticsSource.bad_level ??
        0;

      return {
        items: data.items,
        pagination,
        statistics: {
          total: statisticsSource.total ?? data.items.length,
          online: statisticsSource.online ?? 0,
          offline: statisticsSource.offline ?? 0,
          unknown: statisticsSource.unknown ?? 0,
          bad_level: Number(badLevel) || 0,
        },
      };
    } catch (error) {
      // Логируем ошибку для отладки с полной информацией
      if (import.meta.env.DEV) {
        if (isAxiosError(error)) {
          console.error('[getONUList] Ошибка запроса:', {
            url,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message,
            config: {
              method: error.config?.method,
              baseURL: error.config?.baseURL,
              headers: error.config?.headers,
            },
          });
          // Дополнительно выводим данные ответа отдельно для удобства
          if (error.response?.data) {
            console.error('[getONUList] Данные ошибки:', JSON.stringify(error.response.data, null, 2));
          }
        } else {
          console.error('[getONUList] Неизвестная ошибка:', error);
        }
      }

      // Для 403 возвращаем пустой список только если это действительно отсутствие прав
      if (isAxiosError(error) && error.response?.status === 403) {
        if (import.meta.env.DEV) {
          console.warn('[getONUList] 403 Forbidden - возможно нет прав доступа или устройство не поддерживает PON');
        }
        return createEmptyOnuListResponse(filter);
      }

      // Для 400 (Bad Request) - это ошибка валидации параметров
      if (isAxiosError(error) && error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'devices:errors.bad_params';
        throw new Error(i18n.t('devices:api.paramValidation', { error: errorMessage }));
      }

      // Для всех остальных ошибок пробрасываем дальше
      throw handleApiError(error);
    }
  },

  getDevicePonPortStats: async (deviceId: number): Promise<DevicePonPortStatsResponse> => {
    return withForbiddenFallback(
      async () => {
        const { data } = await api.get<DevicePonPortsRawResponse>(`/devices/${deviceId}/pon/ports`);
        const ports: DevicePonPortStat[] = (data.ports ?? []).map((port) => ({
          oltIfIndex:
            normalizeNumber(port.olt_if_index) ??
            normalizeNumber(port.oltIfIndex) ??
            undefined,
          ifaceName: port.iface_name ?? port.ifaceName ?? undefined,
          total: normalizeNumber(port.total) ?? 0,
          online: normalizeNumber(port.online) ?? 0,
          offline: normalizeNumber(port.offline) ?? 0,
          unknown: normalizeNumber(port.unknown) ?? 0,
          badLevel:
            normalizeNumber(port.bad_level) ??
            normalizeNumber(port.badLevel) ??
            0,
          lastSeen: port.last_seen ?? port.lastSeen ?? undefined,
        }));

        const summaryRaw = data.summary;
        const summary = summaryRaw
          ? {
            ports: normalizeNumber(summaryRaw.ports) ?? ports.length,
            totalOnu:
              normalizeNumber(summaryRaw.total_onu) ??
              normalizeNumber(summaryRaw.totalOnu) ??
              0,
            onlineOnu:
              normalizeNumber(summaryRaw.online_onu) ??
              normalizeNumber(summaryRaw.onlineOnu) ??
              0,
            offlineOnu:
              normalizeNumber(summaryRaw.offline_onu) ??
              normalizeNumber(summaryRaw.offlineOnu) ??
              0,
            unknownOnu:
              normalizeNumber(summaryRaw.unknown_onu) ??
              normalizeNumber(summaryRaw.unknownOnu) ??
              0,
            badLevelOnu:
              normalizeNumber(summaryRaw.bad_level_onu) ??
              normalizeNumber(summaryRaw.badLevelOnu) ??
              0,
            updatedAt: summaryRaw.updated_at ?? summaryRaw.updatedAt ?? undefined,
          }
          : undefined;

        return {
          deviceId: normalizeNumber(data.device_id) ?? deviceId,
          ports,
          summary,
        };
      },
      () => createEmptyPonPortStatsResponse(deviceId),
    );
  },

  getDevicePonMetrics: async (deviceId: number): Promise<DevicePonMetricsResponse> => {
    return withNotFoundFallback(
      async () => {
        const response = await api.get<DevicePonMetricsResponse>(`/devices/${deviceId}/pon/metrics`);
        return response.data;
      },
      () => ({
        data: {},
        meta: { measurements: {} },
      }),
    );
  },

  getInventoryIntegrations: async (
    deviceId: number,
    port?: number,
  ): Promise<DeviceInventoryIntegration[]> => {
    const params = new URLSearchParams();
    if (typeof port === 'number') {
      params.set('port', String(port));
    }
    const query = params.toString();
    const url = query
      ? `/devices/${deviceId}/inventory-integrations?${query}`
      : `/devices/${deviceId}/inventory-integrations`;
    const { data } = await api.get<DeviceInventoryIntegration[]>(url);
    return data;
  },

  createInventoryIntegration: async (
    deviceId: number,
    payload: CreateInventoryIntegrationPayload,
  ): Promise<void> => {
    await api.post(`/devices/${deviceId}/inventory-integrations`, {
      inventoryId: payload.inventoryId,
      port: payload.port,
      ...(payload.typeId != null ? { typeId: payload.typeId } : {}),
    });
  },

  deleteInventoryIntegration: async (deviceId: number, inventoryId: number): Promise<void> => {
    await api.delete(`/devices/${deviceId}/inventory-integrations/${inventoryId}`);
  },

  getDeviceCommutations: async (
    deviceId: number,
    port?: number,
  ): Promise<DeviceCommutationListItem[]> => {
    const params = new URLSearchParams();
    if (typeof port === 'number') {
      params.set('port', String(port));
    }
    const query = params.toString();
    const url = query
      ? `/devices/${deviceId}/commutations?${query}`
      : `/devices/${deviceId}/commutations`;
    const { data } = await api.get<DeviceCommutationListItem[]>(url);
    return data;
  },

  getONUInfoByDevice: async (deviceId: number): Promise<ONUDetailsResponse | null> => {
    return withForbiddenFallback(
      async () => {
        const { data } = await api.get<ONUDetailsResponse>(`/devices/${deviceId}/onu-info`);
        return data;
      },
      () => null,
    );
  },

  getONUDetails: async (deviceId: number, identifier: string): Promise<ONUDetailsResponse> => {
    const encoded = encodeURIComponent(identifier);
    const { data } = await api.get<ONUDetailsResponse>(`/devices/${deviceId}/onu/${encoded}`);
    return data;
  },

  getONULevelHistory: async (
    deviceId: number,
    identifier: string,
    params?: { date_from?: number; date_to?: number; limit?: number },
  ): Promise<ONULevelHistoryResponse> => {
    const encoded = encodeURIComponent(identifier);
    const queryParams = new URLSearchParams();
    if (params?.date_from) queryParams.append('date_from', String(params.date_from));
    if (params?.date_to) queryParams.append('date_to', String(params.date_to));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    const query = queryParams.toString();
    const url = `/devices/${deviceId}/onu/${encoded}/level-history${query ? `?${query}` : ''}`;


    try {
      const { data } = await api.get<ONULevelHistoryResponse>(url);


      return data;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[getONULevelHistory] Error:', { url, error, response: (error as any)?.response });
      }
      throw error;
    }
  },

  // PON калькулятор - схемы
  getPonSchemes: async (deviceId: number) => {
    const { data } = await api.get<{ schemes: Array<{ id: number; name: string; nodes: unknown[]; edges: unknown[]; created_at: string; updated_at: string }> }>(`/devices/${deviceId}/pon/schemes`);
    return data.schemes;
  },

  getPonScheme: async (deviceId: number, schemeId: number) => {
    const { data } = await api.get<{ scheme: { id: number; name: string; nodes: unknown[]; edges: unknown[]; created_at: string; updated_at: string } }>(`/devices/${deviceId}/pon/schemes/${schemeId}`);
    return data.scheme;
  },

  savePonScheme: async (deviceId: number, scheme: { name: string; nodes: unknown[]; edges: unknown[] }) => {
    const { data } = await api.post<{ scheme: { id: number; name: string; nodes: unknown[]; edges: unknown[]; created_at: string; updated_at: string } }>(`/devices/${deviceId}/pon/schemes`, scheme);
    return data.scheme;
  },

  updatePonScheme: async (deviceId: number, schemeId: number, scheme: { name: string; nodes: unknown[]; edges: unknown[] }) => {
    const { data } = await api.put<{ scheme: { id: number; name: string; nodes: unknown[]; edges: unknown[]; created_at: string; updated_at: string } }>(`/devices/${deviceId}/pon/schemes/${schemeId}`, scheme);
    return data.scheme;
  },

  deletePonScheme: async (deviceId: number, schemeId: number): Promise<void> => {
    await api.delete(`/devices/${deviceId}/pon/schemes/${schemeId}`);
  },

  // PON управление ONU
  rebootONU: async (deviceId: number, onuId: string): Promise<{ success: boolean; message: string; task_id?: string }> => {
    const encoded = encodeURIComponent(onuId);
    const { data } = await api.post<{ success: boolean; message: string; task_id?: string }>(`/devices/${deviceId}/onu/${encoded}/reboot`);
    return data;
  },

  setONUProfile: async (deviceId: number, onuId: string, profile: string): Promise<{ success: boolean; message: string; task_id?: string }> => {
    const encoded = encodeURIComponent(onuId);
    const { data } = await api.post<{ success: boolean; message: string; task_id?: string }>(`/devices/${deviceId}/onu/${encoded}/profile`, { profile });
    return data;
  },

  // Получить список профилей ONU с конкретного OLT
  getONUProfiles: async (deviceId: number): Promise<{ profiles: Array<{ id: string; name: string }> }> => {
    const { data } = await api.get<{ profiles: Array<{ id: string; name: string }> }>(`/devices/${deviceId}/pon/profiles`);
    return data;
  },

  updateONUNotes: async (deviceId: number, identifier: string, notes: string): Promise<{ success: boolean; message: string }> => {
    const encoded = encodeURIComponent(identifier);
    const { data } = await api.put<{ success: boolean; message: string }>(`/devices/${deviceId}/onu/${encoded}/notes`, { notes });
    return data;
  },

  getONUMetrics: async (deviceId: number, identifier: string): Promise<ONUMetricsResponse> => {
    const encoded = encodeURIComponent(identifier);
    const { data } = await api.get<ONUMetricsResponse>(`/devices/${deviceId}/onu/${encoded}/metrics`);
    return data;
  },

  // Получить LIVE метрики ONU через прямой SNMP запрос к оборудованию
  getONULiveMetrics: async (deviceId: number, identifier: string): Promise<ONULiveMetricsResponse> => {
    const encoded = encodeURIComponent(identifier);
    const { data } = await api.get<ONULiveMetricsResponse>(`/devices/${deviceId}/onu/${encoded}/live`);
    return data;
  },

  // PON карта - данные для отображения на карте
  getPonMapData: async (deviceId: number) => {
    const { data } = await api.get<{
      devices: Array<{
        id: number;
        device_id: number;
        type: 'olt' | 'onu';
        name: string;
        coordinates: [number, number];
        status?: 'online' | 'offline' | 'unknown';
        level?: number;
        port?: string;
      }>;
      fibers: Array<{
        id: string;
        olt_device_id: number;
        onu_device_id: number;
        olt_coordinates: [number, number];
        onu_coordinates: [number, number];
        port?: string;
        level?: number;
      }>;
    }>(`/devices/${deviceId}/pon/map`);
    return data;
  },

  // Radio Peers - список подключенных радиостанций
  getRadioPeers: async (
    deviceId: number,
    filter?: RadioPeerFilter,
  ): Promise<RadioPeerListResponse> => {
    const params = new URLSearchParams();

    // Обязательные параметры пагинации
    params.append('page', String(filter?.page || 1));
    params.append('limit', String(filter?.limit || 50));

    // Опциональные параметры
    if (filter?.query && filter.query.trim()) {
      params.append('query', filter.query.trim());
    }

    if (filter?.level_min !== undefined && filter.level_min !== null) {
      params.append('level_min', String(filter.level_min));
    }

    if (filter?.level_max !== undefined && filter.level_max !== null) {
      params.append('level_max', String(filter.level_max));
    }

    // Сортировка
    if (filter?.sort_by) {
      params.append('sort_by', filter.sort_by);
    }

    if (typeof filter?.sort_desc === 'boolean') {
      params.append('sort_desc', String(filter.sort_desc));
    }

    const query = params.toString();
    const url = `/devices/${deviceId}/radio/peers${query ? `?${query}` : ''}`;


    try {
      const { data } = await api.get<RadioPeerListResponse>(url);
      return data;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[getRadioPeers] Ошибка запроса:', {
          url,
          status: isAxiosError(error) ? error.response?.status : undefined,
          message: isAxiosError(error) ? error.message : String(error),
        });
      }

      // QueryErrorAlert различает отказ в доступе и сбой загрузки только при
      // сохранённом HTTP-статусе; общий обработчик старого API его теряет.
      if (isAxiosError(error) && error.response?.status === 403) {
        throw error;
      }

      throw handleApiError(error);
    }
  },

  // VLAN управление (из БД)
  getDeviceVlans: async (deviceId: number): Promise<DeviceVlansResponse> => {
    const { data } = await api.get<DeviceVlansResponse>(`/devices/${deviceId}/vlan`);
    return data;
  },

  createVlan: async (deviceId: number, request: CreateVlanRequest): Promise<CreateVlanResponse> => {
    const { data } = await api.post<CreateVlanResponse>(`/devices/${deviceId}/vlan`, request);
    return data;
  },

  updateVlan: async (
    deviceId: number,
    vlanId: number,
    request: UpdateVlanRequest,
  ): Promise<UpdateVlanResponse> => {
    const { data } = await api.put<UpdateVlanResponse>(`/devices/${deviceId}/vlan/${vlanId}`, request);
    return data;
  },

  deleteVlan: async (deviceId: number, vlanId: number): Promise<void> => {
    await api.delete(`/devices/${deviceId}/vlan/${vlanId}`);
  },

  // Инвалидировать кеш интерфейсов
  invalidateInterfacesCache: async (deviceId: number): Promise<{ message: string; device_id: number }> => {
    const { data } = await api.post(`/devices/${deviceId}/interfaces/invalidate`);
    return data;
  },

  // ✅ PHASE 1: Ping device
  pingDevice: async (deviceId: number): Promise<PingResponse> => {
    const { data } = await api.post<PingResponse>(`/devices/${deviceId}/poll/ping`);
    return data;
  },

  // ✅ PHASE 2: Get SNMP info
  getSnmpInfo: async (deviceId: number): Promise<SnmpInfo> => {
    const { data } = await api.get<SnmpInfo>(`/devices/${deviceId}/snmp-info`, {
      timeout: 125000,
      skipRetry: true,
    });
    return data;
  },

  // Device Statistics
  getDeviceStatistics: async (deviceId: number): Promise<DeviceStatistics> => {
    const { data } = await api.get<DeviceStatistics>(`/devices/${deviceId}/statistics`);
    return data;
  },

  // Device Health
  getDeviceHealth: async (deviceId: number): Promise<DeviceHealth> => {
    const { data } = await api.get<DeviceHealth>(`/devices/${deviceId}/health`);
    return data;
  },

  // Device Configuration
  backupDeviceConfiguration: async (
    deviceId: number,
    request: BackupDeviceConfigurationRequest,
  ): Promise<BackupDeviceConfigurationResponse> => {
    const { data } = await api.post<BackupDeviceConfigurationResponse>(
      `/devices/${deviceId}/config/backup`,
      request,
    );
    return data;
  },

  restoreDeviceConfiguration: async (
    deviceId: number,
    request: RestoreDeviceConfigurationRequest,
  ): Promise<RestoreDeviceConfigurationResponse> => {
    const { data } = await api.post<RestoreDeviceConfigurationResponse>(
      `/devices/${deviceId}/config/restore`,
      request,
    );
    return data;
  },

  getDeviceConfigurationHistory: async (
    deviceId: number,
    limit?: number,
  ): Promise<GetDeviceConfigurationHistoryResponse> => {
    const params = limit ? { limit } : {};
    const { data } = await api.get<GetDeviceConfigurationHistoryResponse>(
      `/devices/${deviceId}/config/history`,
      { params },
    );
    return data;
  },
};
